import {
  GraphMember,
  Relationship,
  Side,
  computeKinship,
} from '@/contexts/FamilyGraphContext';
import { generationTierFromKinship } from '@/lib/treeGenerationAuthority';
import { resolveViewerAnchor } from '@/lib/familyPlacement';
import {
  commonTreeBranchPrefix,
} from '@/lib/treeBranchPathAuthority';

import type {
  TreeRuntimeBranchLayoutAuthoritySnapshot,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutAuthoritySnapshot';
import {
  TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,
  isTreeRuntimeCanonicalBranchCounterfactualShadowPlanInternallyConsistent,
  type TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
} from '@/governor/tree-runtime/TreeRuntimeCanonicalBranchCounterfactualShadowProjection';
import {
  createTreeRuntimeBranchLayoutInteractionShadowAuthority,
  isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent,
  isTreeRuntimeCanonicalBranchCounterfactualShadowPlanAuthorityCoherent,
  type TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutInteractionCounterfactualMatrix';
import {
  resolveCanonicalTreeGeometryBranchRoot,
  type CanonicalTreeGeometryBranchRoot,
} from '@/governor/tree/CanonicalTreeGeometryBranchRootAuthority';
import {
  resolveCanonicalTreeGeometryUnitOwnership,
} from '@/governor/tree/CanonicalTreeGeometryUnitOwnershipAuthority';
import {
  planCanonicalTreeConnectorCorridor,
  type CanonicalTreeConnectorPoint,
  type CanonicalTreeConnectorRoot,
  type CanonicalTreeConnectorRouteStatus,
  type CanonicalTreeViewerTerminalEligibilityEvidence,
} from '@/governor/tree/CanonicalTreeConnectorCorridorAuthority';
import {
  planCanonicalTreeGeometryRootEnvelopes,
  type CanonicalTreeGeometryAncestryRoot,
} from '@/governor/tree/CanonicalTreeGeometryRootEnvelopeAuthority';
import type {
  CanonicalTreeGeometryRole,
} from '@/governor/tree/CanonicalTreeGeometryRoleAuthority';
import type {
  CanonicalTreeRelationshipEdge,
} from '@/governor/tree/CanonicalTreeRelationshipEdge';
import {
  planCanonicalTreeLateralConnector,
} from '@/governor/tree/CanonicalTreeLateralConnectorAuthority';

/**
 * STRICT GENERATIONAL PYRAMID LAYOUT
 * ─────────────────────────────────────────────────────────────────────────
 * Pure presentation layer. NEVER mutates parent_ids, names, sides, stored
 * relationships, or any other DB-backed field. Reads members + viewerId
 * and returns a deterministic set of (x, y) positioned cards arranged in
 * a clean family pyramid:
 *
 *     tier 3 ─ Great-Grandparents          (top)
 *     tier 2 ─ Grandparents
 *     tier 1 ─ Parents · Aunts · Uncles
 *     tier 0 ─ YOU · siblings · spouse · cousins                   (anchor)
 *     tier-1 ─ Children · Nieces · Nephews · Cousin's children
 *     tier-2 ─ Grandchildren                                    (bottom)
 *
 * RULES (verbatim from the spec):
 *   1. Every person assigned a generation index (computed from the kinship
 *      engine: parent_ids walks → tier).
 *   2. Viewer-centered: YOU is always at x=0. Siblings flank YOU.
 *   3. Couples — two members linked via spouse_id are rendered as a
 *      LOCKED PAIR side-by-side with a small fixed gap. Children of an
 *      explicit couple connect from the midpoint between the two parents.
 *      Co-parents who are NOT linked via spouse_id stay independent;
 *      each co-parent gets its own edge to the shared child (no heart).

 *      Cousins under their aunt/uncle, NOT under YOU.
 *      Niblings under siblings.
 *      Grandchildren under their direct parent.
 *   5. Maternal branch (mom's side) renders LEFT of center; paternal RIGHT.
 *   6. Each parent's children form a horizontal cluster directly beneath
 *      them. Clusters expand outward — no flattening.
 *   7. Minimum spacing enforced; no overlaps.
 *   8. Vertical lines for parent → child. Children connect from the
 *      midpoint of their two parents.
 *   9. Layout is deterministic and stable — same data → same structure.
 */

// ─── Geometry ───────────────────────────────────────────────────────────
export const CARD_W = 168;
export const CARD_H = 96;
/** Vertical distance between generation rows (strict, no exceptions). */
export const ROW_GAP = 184;
/** Horizontal gap between locked couple partners. */
/** Horizontal gap between locked couple partners. */
export const COUPLE_GAP = 24;
/** Within-cluster horizontal spacing between non-couple cards.
 *  ISSUE 2 FIX (Dec 2026): bumped 44 → 72 so adjacent same-tier cards
 *  on dense mobile views no longer touch / overlap. Combined with the
 *  per-tier collision relaxation below this guarantees a minimum
 *  edge-to-edge gap of 72px between every visible card on every tier. */
export const CLUSTER_GAP = 72;

/**
 * CC-TREE-LIVE-003G3R
 *
 * Local members inside one canonical family cluster may pack more tightly
 * than separate family clusters. These values are presentation-only.
 */
export const LOCAL_FAMILY_GAP = 44;
export const FAMILY_CLUSTER_GAP = 72;

/** Cushion between separate sub-clusters / sibling-of-spine branches.
 *  ISSUE 2 FIX: bumped 88 → 120 so cousin / aunt branches don't crowd
 *  the parent column on phones. Canvas expands; pinch-zoom + auto-fit
 *  re-scale the result so the user still sees the whole pyramid. */
export const BRANCH_GAP = 120;

/**
 * Protected maternal/paternal root-envelope and connector-boundary gap.
 * Cluster compaction may never weaken this safety boundary.
 */
export const ROOT_BOUNDARY_GAP = 240;
/** Tight gap between two non-coupled CO-PARENTS of the same child.
 *  Used when two members share a child via parent_ids but are NOT
 *  linked via spouse_id (separated / never-coupled / blended-family
 *  co-parents). Per the global "parent-pair clustering" spec:
 *   • The pair must render TOGETHER above their shared child.
 *   • Their gap must be CLOSER to each other than to siblings /
 *     aunts / uncles / unrelated same-tier relatives on the row.
 *  We deliberately keep this slightly larger than COUPLE_GAP (24)
 *  so the layout still telegraphs "not a heart-line couple" — no
 *  spouse_id is stored, so the heart connector does not render —
 *  but visually small enough that the two cards read as a parent
 *  pair, not as scattered same-tier relatives. */
export const CO_PARENT_CLUSTER_GAP = 36;



// ─── Public types (mirror FamilyTreeView's existing shape) ──────────────
export interface PyramidCard {
  member: GraphMember | null;
  memberId: string | null;
  isYou?: boolean;
  x: number;
  y: number;
  /** Tier: positive = ancestors, 0 = viewer row, negative = descendants. */
  tier: number;
  side: 'mother' | 'father' | 'self';
  geometryRoot: CanonicalTreeGeometryBranchRoot;

  /**
   * Presentation-only canonical unit lineage.
   *
   * This is derived from the already-governed layout path.
   * It cannot create topology, kinship, generation, side,
   * placement, routing, or mutation authority.
   */
  clusterKey?: string | null;

  needsConnection?: boolean;
}

export interface PyramidEdge {
  fromMemberIds: string[];
  toMemberId: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  side: 'mother' | 'father' | 'self';
  verified: boolean;
  key: string;
  routeRoot: CanonicalTreeConnectorRoot;
  routeStatus: CanonicalTreeConnectorRouteStatus;

  /*
   * Private diagnostic metadata only.
   * It is not geometry and grants no routing authority.
   *
   * null means this edge never entered the ancestry
   * connector-corridor viewer-terminal predicate.
   */
  viewerTerminalEligibility:
    CanonicalTreeViewerTerminalEligibilityEvidence | null;

  routePoints: CanonicalTreeConnectorPoint[];
  rejectedObstacleMemberIds: string[];
}

export interface PyramidSpouseLink {
  aMemberId: string;
  bMemberId: string;
  a: { x: number; y: number };
  b: { x: number; y: number };
}

export interface PyramidBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  w: number;
  h: number;
}

export interface ViewerCenterEvidence {
  requestedViewerMemberId: string | null;
  resolvedStructuralViewerId: string | null;
  renderedYouMemberId: string | null;
  renderedYouX: number | null;
  renderedYouY: number | null;
  centeredMemberId: string | null;
  isRenderedCenterCorrect: boolean;
  failureReason: string | null;
}

export interface PyramidLayoutResult {
  cards: PyramidCard[];
  edges: PyramidEdge[];
  spouseLinks: PyramidSpouseLink[];
  bounds: PyramidBounds;
  viewerCenterEvidence: ViewerCenterEvidence;
}

// ─── Internal types ──────────────────────────────────────────────────────
type SideKey = 'mother' | 'father' | 'self';

interface Unit {
  id: string;
  members: GraphMember[]; // 1 or 2
  tier: number;
  side: SideKey;
  geometryRoot: CanonicalTreeGeometryBranchRoot;
  lineageKey?: string;
  /** For couples: the spec says couples are locked. Defaults [left, right]. */
  x?: number; // unit center (couple midpoint when 2 members)
  y?: number;
}

/*
 * Canonical family-cluster identity for spacing only.
 *
 * ancestor.0.0
 * ancestor.0.0.1
 * ancestor.0.0.child.2
 *
 * all belong to ancestor.0.0.
 *
 * No name, member id, stored label, coordinate, or color participates.
 */
const canonicalFamilyClusterPrefix = (
  lineageKey:
    string | null | undefined,
): string | null => {
  const normalized =
    (lineageKey ?? '').trim();

  if (
    !/^ancestor(?:\.\d+)+(?:\.child\.\d+)*(?:\.partner)?$/.test(
      normalized,
    )
  ) {
    return null;
  }

  const tokens =
    normalized.split('.');

  const numeric: string[] =
    [];

  for (
    const token of
    tokens.slice(1)
  ) {
    if (
      token === 'child' ||
      token === 'partner'
    ) {
      break;
    }

    if (!/^\d+$/.test(token)) {
      return null;
    }

    numeric.push(token);

    if (numeric.length === 2) {
      break;
    }
  }

  if (numeric.length === 0) {
    return null;
  }

  return [
    'ancestor',
    ...numeric,
  ].join('.');
};

const sameCanonicalFamilyCluster = (
  left: Unit,
  right: Unit,
): boolean => {
  const leftCluster =
    canonicalFamilyClusterPrefix(
      left.lineageKey,
    );

  const rightCluster =
    canonicalFamilyClusterPrefix(
      right.lineageKey,
    );

  return (
    leftCluster !== null &&
    leftCluster === rightCluster
  );
};

const sideOf = (m: GraphMember): SideKey => {
  if (m.side === 'mother') return 'mother';
  if (m.side === 'father') return 'father';
  return 'self';
};

const widthOf = (u: Unit) =>
  u.members.length === 2 ? 2 * CARD_W + COUPLE_GAP : CARD_W;

/** Map kinship-engine code → tier index. */
const tierFromKinship = (
  members: GraphMember[],
  viewerId: string,
  m: GraphMember,
): number | null => {
  try {
    return generationTierFromKinship(
      computeKinship(members, viewerId, m.id),
    );
  } catch {
    return null;
  }
};

/**
 * Stored-relationship fallback when the kinship engine returns null/unknown.
 *
 * STRUCTURAL-TRUTH RULE — relationships that are PURELY DERIVED from a
 * shared-ancestor walk (aunt-uncle / cousin / cousin-child /
 * great-grandparent) must NOT be placed on the tree from their stored
 * label alone. The stored label is just a hint the user typed; the only
 * way to KNOW someone is your aunt is by walking parent_ids and finding
 * a shared grandparent. When that walk fails (kinship engine returned
 * 'unknown' / 'extended'), returning a tier here would land an
 * unconnected aunt/cousin on the parents/cousins row WITHOUT a parent
 * edge — exactly the "unrelated relatives pushed into a row they don't
 * belong to" failure mode the spec calls out (rule #5).
 *
 * For these inferred-only relationship types we return null so the
 * member falls into the overflow / "Needs connection" row instead.
 * The user can then open that card and use Set Relationship to link
 * the missing parent_ids, after which the kinship engine will place
 * them correctly without any data being changed automatically.
 *
 * Direct-label relationships (mother / father / step-parent /
 * adopted-parent / grandparent / sibling / spouse / child) still
 * accept the stored tier — those types CAN stand on their own visual
 * tier even without a complete parent_ids chain, because the label
 * itself ("Mother") tells us exactly which generation to draw them on
 * (the spine member's tier-1 row). They simply render without the
 * vertical parent-edge line until the user fills in their parents.
 */
const tierFromStored = (rel: Relationship): number | null => {
  switch (rel) {
    case 'great-grandparent':
      // Inferred-only: requires a parent_ids walk back to a grandparent.
      return null;
    case 'grandparent':
      // Direct label — user explicitly said "this is my grandparent".
      // Place on tier 2; parent-edge line is drawn only if parent_ids
      // structurally support it.
      return 2;
    case 'mother':
    case 'father':
    case 'step-parent':
    case 'adopted-parent':
      return 1;
    case 'aunt-uncle':
      // Inferred-only: must share a parent with one of the viewer's
      // parents. If kinship engine couldn't prove that → Needs connection.
      return null;
    case 'sibling':
      return 0;
    case 'spouse':
      // SPOUSE/PARTNER FIX (May 2026 — son-in-law rendering):
      //   A row stored as relationship='spouse' is NEVER guaranteed to be
      //   YOUR spouse. In-law spouse rows (Son-in-law / Daughter-in-law /
      //   Brother-/Sister-in-law) are saved with relationship='spouse'
      //   too, but their spouse_id points at one of YOUR relatives — not
      //   at YOU. Returning tier 0 here would land them on YOUR row even
      //   though structurally they belong on their partner's tier (e.g.
      //   son-in-law sits on tier -1 next to your daughter, with the
      //   heart-line connector).
      //
      //   Returning null here lets Step 3b ("spouse-anchored tier
      //   inheritance") in computePyramidLayout pick the row up and
      //   inherit its spouse partner's tier — which is the correct seat
      //   for every spouse/in-law variant in one rule:
      //     • Viewer's own spouse → kinship engine returns 'spouse' code,
      //       tierFromKinship returns 0, this fallback is never reached.
      //     • Any other spouse-mode row (son-/daughter-/sis-/bro-in-law)
      //       → kinship engine returns 'unknown' (no parent_ids path,
      //       no direct spouse_id link to viewer), this fallback returns
      //       null, Step 3b inherits their partner's tier, buildCouples
      //       forms the locked couple unit, the heart line renders, and
      //       no overflow row is created.
      //   The previous version returned 0 unconditionally — exactly why
      //   Terrance Lewis Sr. floated at the bottom in "Needs connection"
      //   instead of beside Chekesha.
      return null;

    case 'cousin':
      // Inferred-only: must descend from an aunt/uncle. If kinship engine
      // couldn't prove that → Needs connection.
      return null;
    case 'child':
      return -1;
    case 'cousin-child':
      // Inferred-only: must descend from a cousin or sibling. If kinship
      // engine couldn't prove that → Needs connection.
      return null;
    case 'unknown':
      return null;
    default:
      return null;
  }
};

/**
 * Build the locked couple-partner map.
 *
 * SPOUSE BEHAVIOR (UNIVERSAL — every couple gets the heart connector):
 *   Two members are paired into a visual couple unit (rendered side-by-
 *   side at the same tier with the heart connector + locked midpoint
 *   for any shared children) when ANY of the following holds:
 *
 *     1. RECIPROCAL spouse_id    (a.spouseId === b.id && b.spouseId === a.id)
 *     2. ONE-SIDED spouse_id     (a.spouseId === b.id && b.spouseId is null)
 *                                — auto-reciprocates for layout purposes
 *                                  without writing to the DB. Per the
 *                                  spec: "If A.spouse_id = B and
 *                                  B.spouse_id is null, auto-reciprocate
 *                                  safely or render the heart from the
 *                                  one-sided edge."
 *     3. REVERSE one-sided       (b.spouseId === a.id && a.spouseId is null)
 *
 *   Couple formation is REJECTED when:
 *     • Either id is missing from the candidate list (i.e. the partner
 *       isn't placeable on the canvas with a derived tier).
 *     • The two ids are the same row.
 *     • The partner already has a DIFFERENT reciprocal spouse on the
 *       canvas (a.spouseId === b.id but b.spouseId === c.id where c≠a).
 *       In that case `a` is treated as a one-sided pointer at someone
 *       else's spouse and we don't form a couple to avoid stealing the
 *       partner from their established couple.
 *     • One of the two members is a structural blood relative of the
 *       other through parent_ids (parent / ancestor / descendant). This
 *       is the bloodline-priority guard — parent-child logic always wins
 *       over an accidental spouse pairing.
 *
 *   Couples render side-by-side with a small fixed gap and the heart-
 *   line connector. Their children connect from the couple midpoint
 *   when both are listed on the child's parent_ids, OR independently
 *   from each side when only one is listed (modern-family rule).
 *
 *   Pure layout — never mutates spouse_id. Stored values are read
 *   exactly as-is. parent_ids semantics, the cycle guard, and the
 *   spouse-as-parent strip in `linkParents` all stay intact.
 *
 *   ACCEPTANCE: Adding a grandmother through grandfather's "+ spouse"
 *   button now produces a couple unit on the grandparents tier with a
 *   heart between them, even when only one side of the spouse_id link
 *   landed in the DB. The same rule applies globally to parents,
 *   grandparents, great-grandparents, children's spouses, in-laws, and
 *   any partner added from any card's "+ spouse" button.
 */
const buildCouples = (
  candidates: GraphMember[],
  _viewerId: string | null,
  byId: Map<string, GraphMember>,
): Map<string, string> => {
  const partner = new Map<string, string>();
  const candidateIdSet = new Set(candidates.map((m) => m.id));

  // Helper — does `pid` already point at a different reciprocated
  // partner (someone other than `selfId`) ? When true, we must NOT
  // couple `selfId` with `pid` because `pid` is already in an
  // established couple with a third party.
  const partnerLockedToSomeoneElse = (
    pid: string,
    selfId: string,
  ): boolean => {
    const partnerRow = byId.get(pid);
    if (!partnerRow) return false;
    const partnerSpouse = partnerRow.spouseId ?? null;
    if (partnerSpouse === null) return false; // partner unset → free
    if (partnerSpouse === selfId) return false; // partner agrees with us
    // partner.spouseId points at someone other than selfId — only treat
    // as "locked to someone else" when that third party also exists in
    // the candidate set (otherwise it's a stale half-link to a deleted
    // / RLS-hidden row that we should ignore).
    return candidateIdSet.has(partnerSpouse);
  };

  // Helper — do these two rows have a parent-child overlap that should
  // block coupling? (bloodline priority).
  const isBlood = (a: GraphMember, b: GraphMember): boolean => {
    const aParents = new Set((a.parentIds ?? []).filter(Boolean));
    const bParents = new Set((b.parentIds ?? []).filter(Boolean));
    return aParents.has(b.id) || bParents.has(a.id);
  };

  // Pass 1 — RECIPROCAL spouse_id pairs. Highest-confidence couples
  // (both rows agree). We process these first so a fully-reciprocated
  // pair always wins over any one-sided link.
  for (const m of candidates) {
    if (partner.has(m.id)) continue;
    const spouseId = m.spouseId ?? null;
    if (!spouseId || spouseId === m.id) continue;
    if (!candidateIdSet.has(spouseId)) continue;
    const partnerRow = byId.get(spouseId);
    if (!partnerRow) continue;
    if (partner.has(spouseId)) continue;
    // Reciprocal only.
    if (partnerRow.spouseId !== m.id) continue;
    if (isBlood(m, partnerRow)) continue;
    partner.set(m.id, spouseId);
    partner.set(spouseId, m.id);
  }

  // Pass 2 — ONE-SIDED spouse_id links (forward direction).
  // m.spouseId points at partnerRow but partnerRow.spouseId is null
  // (or partnerRow.spouseId points at m — already handled above).
  // Per the spec: render the heart from the one-sided edge. We DO NOT
  // write back to the DB — auto-reciprocation is purely visual here.
  for (const m of candidates) {
    if (partner.has(m.id)) continue;
    const spouseId = m.spouseId ?? null;
    if (!spouseId || spouseId === m.id) continue;
    if (!candidateIdSet.has(spouseId)) continue;
    if (partner.has(spouseId)) continue;
    const partnerRow = byId.get(spouseId);
    if (!partnerRow) continue;
    // Skip if the partner is already locked into a different couple
    // via their own reciprocal spouse_id pointing elsewhere.
    if (partnerLockedToSomeoneElse(spouseId, m.id)) continue;
    if (isBlood(m, partnerRow)) continue;
    partner.set(m.id, spouseId);
    partner.set(spouseId, m.id);
  }

  // Pass 3 — REVERSE one-sided links. m.spouseId is null but some
  // other row's spouseId points at m. Couple them (reverse direction).
  // This is the case where the grandmother was added with spouse_id
  // pointing at grandfather, but the write to grandfather's row didn't
  // land (RLS blip / partial save / different owner_user_id).
  for (const m of candidates) {
    if (partner.has(m.id)) continue;
    if ((m.spouseId ?? null) !== null) continue; // forward already handled
    // Find any candidate whose spouseId points at m.
    let pickedId: string | null = null;
    for (const other of candidates) {
      if (other.id === m.id) continue;
      if (partner.has(other.id)) continue;
      if ((other.spouseId ?? null) !== m.id) continue;
      if (isBlood(m, other)) continue;
      // First match wins — deterministic since `candidates` order is
      // stable across renders.
      pickedId = other.id;
      break;
    }
    if (pickedId) {
      partner.set(m.id, pickedId);
      partner.set(pickedId, m.id);
    }
  }

  return partner;
};





/**
 * Order a couple's two members so the side-aligned partner sits on the
 * "outboard" side (mom-left, dad-right). For non-side couples (your own
 * spouse, or an ancestor couple with mixed sides), preserve a deterministic
 * order by stored relationship + name.
 */
const orderedCoupleMembers = (members: GraphMember[]): GraphMember[] => {
  if (members.length !== 2) return members.slice();
  const [a, b] = members;
  // Mom-side member goes left, dad-side right.
  if (a.side === 'mother' && b.side !== 'mother') return [a, b];
  if (b.side === 'mother' && a.side !== 'mother') return [b, a];
  if (a.side === 'father' && b.side !== 'father') return [b, a];
  if (b.side === 'father' && a.side !== 'father') return [a, b];
  // Stored mother before father.
  if (a.relationship === 'mother' && b.relationship === 'father') return [a, b];
  if (b.relationship === 'mother' && a.relationship === 'father') return [b, a];
  // Deterministic: alphabetical.
  return [a, b].sort((x, y) => x.name.localeCompare(y.name));
};

// ─── Main entry point ────────────────────────────────────────────────────
export interface PyramidLayoutInput {
  /** Canonical graph used for kinship, generations, branches, and edges. */
  structuralMembers: GraphMember[];
  /** Viewer-projected rows allowed to render on this surface. */
  visibleMembers: GraphMember[];
  /** Immutable branch/layout authority assembled by the caller. */
  branchLayoutAuthority:
    TreeRuntimeBranchLayoutAuthoritySnapshot;
  /** Optional canonical role authority. */
  canonicalGeometryRoles?:
    ReadonlyMap<string, CanonicalTreeGeometryRole>;
  /** Optional canonical edge evidence for nonancestral connector geometry. */
  canonicalEdges?:
    readonly CanonicalTreeRelationshipEdge[];
  centeredMemberId?: string | null;
  /** Display name to label the YOU card. */
  youName: string;
}

export function computePyramidLayout(
  input: PyramidLayoutInput,
): PyramidLayoutResult {
  return computePyramidLayoutInternal(
    input,
    null,
  );
}

export function computePyramidLayoutCanonicalBranchShadow(
  input: PyramidLayoutInput,
  shadowPlan:
    TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
): PyramidLayoutResult {
  if (
    shadowPlan.schema !==
      TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA ||
    !isTreeRuntimeCanonicalBranchCounterfactualShadowPlanInternallyConsistent(
      shadowPlan,
    )
  ) {
    throw new Error(
      'Invalid canonical branch counterfactual shadow plan.',
    );
  }

  return computePyramidLayoutInternal(
    input,
    shadowPlan,
  );
}

/*
 * CC-TREE-LIVE-003G3E4 — matrix cell C
 *
 * Production semantic branch authority is held constant.
 * Only the canonical-compatible layout-path map is substituted
 * inside this non-rendered shadow invocation.
 */
export function computePyramidLayoutCanonicalLayoutShadow(
  input: PyramidLayoutInput,
  matrixPlan:
    TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
): PyramidLayoutResult {
  if (
    !isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent(
      matrixPlan,
      input.branchLayoutAuthority,
    )
  ) {
    throw new Error(
      'Invalid or stale 003G3E4 matrix plan for cell C.',
    );
  }

  const shadowAuthority =
    createTreeRuntimeBranchLayoutInteractionShadowAuthority(
      input.branchLayoutAuthority,
      matrixPlan,
    );

  return computePyramidLayoutInternal(
    {
      ...input,
      branchLayoutAuthority:
        shadowAuthority,
    },
    null,
  );
}

/*
 * CC-TREE-LIVE-003G3E4 — matrix cell D
 *
 * Both controlled shadow variables are applied:
 * canonical semantic branch + canonical-compatible layout path.
 * Production selectors and readiness metadata remain unchanged.
 */
export function computePyramidLayoutCanonicalBranchAndLayoutShadow(
  input: PyramidLayoutInput,
  matrixPlan:
    TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
  semanticPlan:
    TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
): PyramidLayoutResult {
  if (
    !isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent(
      matrixPlan,
      input.branchLayoutAuthority,
    ) ||
    !isTreeRuntimeCanonicalBranchCounterfactualShadowPlanAuthorityCoherent(
      semanticPlan,
      input.branchLayoutAuthority,
    ) ||
    semanticPlan.substitutedMemberCount !==
      matrixPlan.semanticSubstitutedMemberCount
  ) {
    throw new Error(
      'Invalid or incoherent 003G3E4 matrix plans for cell D.',
    );
  }

  const shadowAuthority =
    createTreeRuntimeBranchLayoutInteractionShadowAuthority(
      input.branchLayoutAuthority,
      matrixPlan,
    );

  return computePyramidLayoutInternal(
    {
      ...input,
      branchLayoutAuthority:
        shadowAuthority,
    },
    semanticPlan,
  );
}

function computePyramidLayoutInternal(
  input: PyramidLayoutInput,
  counterfactualShadowPlan:
    TreeRuntimeCanonicalBranchCounterfactualShadowPlan | null,
): PyramidLayoutResult {
  const {
    structuralMembers,
    visibleMembers,
    branchLayoutAuthority,
    canonicalGeometryRoles =
      new Map<string, CanonicalTreeGeometryRole>(),
    canonicalEdges = [],
    centeredMemberId = null,
  } = input;

  const viewerMemberId =
    branchLayoutAuthority.viewerMemberId;

  // ─── Step 1: Structural graph + presentation visibility ───────────────
  // Canonical rows remain the only source for kinship, generations,
  // parent/spouse topology, branch paths, and connector authority.
  const members = structuralMembers;

  const displayById = new Map<string, GraphMember>(
    visibleMembers.map((member) => [member.id, member]),
  );
  const visibleIdSet = new Set(displayById.keys());

  const displayMemberFor = (member: GraphMember): GraphMember =>
    displayById.get(member.id) ?? member;

  /*
   * CC-TREE-LIVE-003G2X
   *
   * Canonical geometry is always built from the complete Tree-visible
   * structural graph.
   *
   * Full / Mother / Father / Extended are presentation projections only.
   * They are forbidden from changing tier computation, parent propagation,
   * units, coordinates, connector construction, routing, or spouse links.
   */
  const visible =
    structuralMembers.filter(
      member =>
        visibleIdSet.has(member.id) &&
        member.approvalStatus !== 'rejected',
    );

  const byId = new Map<string, GraphMember>();
  for (const m of structuralMembers) byId.set(m.id, m);

  // ─── Step 2: Resolve viewer anchor ────────────────────────────────────
  const anchor = resolveViewerAnchor(members, viewerMemberId);
  const viewerId = anchor?.id ?? null;

  if (viewerMemberId && !anchor) {
    const bounds: PyramidBounds = {
      minX: -CARD_W / 2,
      maxX: CARD_W / 2,
      minY: -CARD_H / 2,
      maxY: CARD_H / 2,
      w: CARD_W,
      h: CARD_H,
    };

    return {
      cards: [],
      edges: [],
      spouseLinks: [],
      bounds,
      viewerCenterEvidence: {
        requestedViewerMemberId: viewerMemberId,
        resolvedStructuralViewerId: null,
        renderedYouMemberId: null,
        renderedYouX: null,
        renderedYouY: null,
        centeredMemberId,
        isRenderedCenterCorrect: false,
        failureReason: 'explicit-viewer-missing',
      },
    };
  }

  const layoutPathSelection =
    branchLayoutAuthority.layoutPath;

  const branchPathById =
    layoutPathSelection.branchPathByMemberId;

  const semanticBranchSelection =
    branchLayoutAuthority.branch;

  const productionSemanticAuthorityReady =
    semanticBranchSelection.source === 'canonical' &&
    (
      semanticBranchSelection.migrationReadiness === 'READY' ||
      semanticBranchSelection.migrationReadiness ===
        'READY-WITH-CANONICAL-ADDITIONS'
    );

  const productionSemanticBranchPathById =
    semanticBranchSelection.canonicalBranchPathByMemberId;

  const geometryMemberOf = (
    member: GraphMember,
  ) => {
    const layoutPath =
      branchPathById.get(member.id) ?? null;

    const useCounterfactualSemanticAuthority =
      counterfactualShadowPlan
        ?.substitutedMemberIds
        .has(member.id) ??
      false;

    const semanticBranchPath =
      useCounterfactualSemanticAuthority
        ? counterfactualShadowPlan
            ?.canonicalSemanticBranchPathByMemberId
            .get(member.id) ??
          null
        : productionSemanticBranchPathById
            .get(member.id) ??
          null;

    if (
      useCounterfactualSemanticAuthority &&
      !semanticBranchPath
    ) {
      throw new Error(
        `Counterfactual semantic path missing for ${member.id}.`,
      );
    }

    const semanticAuthorityReady =
      useCounterfactualSemanticAuthority
        ? true
        : productionSemanticAuthorityReady;

    const geometry =
      resolveCanonicalTreeGeometryBranchRoot({
        layoutPath,
        semanticBranchPath,
        semanticAuthorityReady,
      });

    return {
      memberId: member.id,
      root: geometry.root,
      layoutPath,
    };
  };

  const geometryOwnershipOf = (
    unitMembers: readonly GraphMember[],
  ) =>
    resolveCanonicalTreeGeometryUnitOwnership({
      members:
        unitMembers.map(geometryMemberOf),
      viewerMemberId: viewerId,
    });

  // ─── Step 3: Compute tier per visible member ──────────────────────────
  // ─── Step 3: Compute tier per visible member ──────────────────────────
  const tierById = new Map<string, number | null>();
  // SPOUSE-OF-VIEWER FIX (May 2026):
  //   The viewer themselves are skipped from the per-member tier loop
  //   (their card is rendered as the synthesized YOU unit at tier 0).
  //   But Step 3b's spouse-anchored inheritance also needs to KNOW the
  //   viewer sits at tier 0 so that a partner whose only connection is
  //   `spouseId === viewerId` (forward) or whose stored row points at
  //   the viewer (reverse) can inherit tier 0 and form the YOU couple
  //   unit with the heart connector. Without this seed, Chekesha's
  //   husband Terrance — whose stored relationship is 'spouse' (returns
  //   null from tierFromStored) and whose only structural link is
  //   spouseId pointing at Chekesha (the viewer) — gets stranded in the
  //   pendingSpouseAnchor list with no tiered partner to inherit from
  //   and lands in the Needs-connection overflow row instead of beside
  //   YOU. Seeding tierById with the viewer at 0 fixes it globally for
  //   every viewer-centered session (original owner OR invite-joined
  //   user) without mutating any data.
  if (viewerId) tierById.set(viewerId, 0);
  const live: GraphMember[] = [];
  // Track who needs spouse-anchored tier inheritance (Pass B below).
  const pendingSpouseAnchor: GraphMember[] = [];
  for (const m of visible) {
    if (viewerId && m.id === viewerId) continue;
    if (
      !viewerId &&
      m.side === 'self' &&
      m.relationship === 'unknown'
    ) {
      // Legacy implicit self-anchor row when no viewerId is set — drawn as
      // YOU card, skip from member layout.
      continue;
    }
    const canonicalRole =
      canonicalGeometryRoles.get(m.id);

    const tier =
      canonicalRole
        ? canonicalRole.tier
        : viewerId
          ? tierFromKinship(
              members,
              viewerId,
              m,
            ) ??
            tierFromStored(
              m.relationship,
            )
          : tierFromStored(
              m.relationship,
            );
    tierById.set(m.id, tier);
    if (tier !== null) {
      live.push(m);
    } else {
      pendingSpouseAnchor.push(m);
    }
  }

  // ─── Step 3b: Spouse-anchored tier inheritance ────────────────────────
  // SPOUSE-ONLY PLACEMENT RULE — a spouse/partner does NOT need parents
  // to be placed on the tree. If the only thing we know about a member
  // is that they are spouse-linked to a relative who DOES have a tier
  // (INCLUDING the viewer at tier 0), anchor them to that spouse's tier.
  // They will then form a couple unit alongside their partner in Step 5
  // (locked side-by-side with a heart line) without ever being marked
  // as "Needs connection" or pushed into the overflow row at the bottom
  // of the canvas.
  //
  // SPOUSE-OF-VIEWER (invited paid users):
  //   When the viewer is a logged-in paid user whose tree centers on
  //   them, their spouse rows are handled here EXACTLY the same way as
  //   any other spouse pairing. The viewer is seeded into tierById at
  //   tier 0 above, so a partner with spouseId === viewer (forward) or
  //   any row reverse-linked to the viewer is anchored to tier 0 and
  //   slotted into the YOU couple unit by Step 5. This means Chekesha's
  //   husband Terrance is now ALWAYS visible beside her when she logs
  //   in, regardless of whether reciprocity is set on his row — same
  //   guarantee that applies to the original tree owner.
  //
  // Pure layout — never mutates parent_ids, spouse_id, side, or stored
  // relationship. The kinship engine, bloodline-priority guards in
  // buildCouples, and parent-edge connectors all stay intact.
  //
  // Iterate to a fixed point so that "spouse-of-a-spouse-of-..." chains
  // resolve. In practice 2 passes are enough (couples don't nest), but
  // we cap at 4 for safety.
  for (let pass = 0; pass < 4; pass++) {
    let changed = false;
    for (const m of pendingSpouseAnchor) {
      if (tierById.get(m.id) !== null && tierById.get(m.id) !== undefined) continue;
      // Forward direction: m.spouseId points at a tiered partner
      // (which now includes the viewer at tier 0).
      let partnerId: string | null = m.spouseId ?? null;
      let partnerTier: number | null | undefined = partnerId
        ? tierById.get(partnerId)
        : undefined;
      if (
        partnerId &&
        (partnerTier === null || partnerTier === undefined)
      ) {
        partnerId = null; // forward partner not yet tiered, try reverse
        partnerTier = undefined;
      }
      // Reverse direction: someone else stored spouse_id pointing at m.
      // The reverse partner can be the viewer too (viewer is seeded at
      // tier 0 in tierById above).
      if (!partnerId) {
        for (const candidate of members) {
          if (candidate.id === m.id) continue;
          if (candidate.spouseId !== m.id) continue;
          if (candidate.approvalStatus === 'rejected') continue;
          const tier = tierById.get(candidate.id);
          if (tier === null || tier === undefined) continue;
          partnerId = candidate.id;
          partnerTier = tier;
          break;
        }
      }
      if (!partnerId || partnerTier === null || partnerTier === undefined) {
        continue;
      }
      tierById.set(m.id, partnerTier);
      live.push(m);
      changed = true;
    }
    if (!changed) break;
  }



  // ─── Step 3c: Structural parent-tier propagation ─────────────────────
  //
  // A visible member referenced through a child's canonical parentIds is
  // structurally a parent even when its stored relationship label does not
  // independently resolve through viewer kinship. This occurs for separated
  // or never-partnered co-parents of descendants.
  //
  // When the child already owns a governed tier and the referenced parent
  // has no tier authority, infer exactly one row above the child:
  //
  //   parentTier = childTier + 1
  //
  // Existing canonical or stored tiers are never overwritten. The pass is
  // iterative so deeper descendant chains resolve without relationship-name
  // guessing. Pure presentation only: no parentIds or relationship data is
  // mutated.
  const visibleMemberIds =
    new Set(
      visible.map(
        member =>
          member.id,
      ),
    );

  const liveMemberIds =
    new Set(
      live.map(
        member =>
          member.id,
      ),
    );

  for (
    let pass = 0;
    pass < Math.max(
      1,
      visible.length,
    );
    pass += 1
  ) {
    let changed =
      false;

    for (const child of visible) {
      const childTier =
        tierById.get(
          child.id,
        );

      if (
        childTier === null ||
        childTier === undefined
      ) {
        continue;
      }

      for (
        const parentId of
        (
          child.parentIds ??
          []
        ).filter(Boolean)
      ) {
        if (
          parentId === viewerId ||
          !visibleMemberIds.has(
            parentId,
          )
        ) {
          continue;
        }

        const currentTier =
          tierById.get(
            parentId,
          );

        if (
          currentTier !== null &&
          currentTier !== undefined
        ) {
          continue;
        }

        const parent =
          byId.get(
            parentId,
          );

        if (
          !parent ||
          parent.approvalStatus === 'rejected'
        ) {
          continue;
        }

        tierById.set(
          parentId,
          childTier + 1,
        );

        if (
          !liveMemberIds.has(
            parentId,
          )
        ) {
          live.push(
            parent,
          );

          liveMemberIds.add(
            parentId,
          );
        }

        changed =
          true;
      }
    }

    if (!changed) break;
  }

  // ─── Step 4: Build couples ────────────────────────────────────────────
  const couplePartner = buildCouples(live, viewerId, byId);

  // ─── Step 5: Build units (singletons + couples) ───────────────────────
  const unitOfMember = new Map<string, Unit>();
  const allUnits: Unit[] = [];

  // YOU's unit. Always exists when viewerId is set; if viewer has a spouse
  // on canvas, the unit holds both.
  //
  // SPOUSE-OF-VIEWER COUPLE DETECTION (May 2026):
  //   buildCouples() iterates `live` to find reciprocal/one-sided spouse
  //   pairs, but the viewer is NEVER in `live` (they're skipped from the
  //   per-member tier loop and rendered as the synthesized YOU card).
  //   That means couplePartner.get(viewerId) is always undefined — even
  //   when the data clearly shows the viewer has a spouse on canvas.
  //
  //   To slot the viewer's spouse into the YOU couple unit, we read the
  //   spouse_id link DIRECTLY here (forward + reverse), matching the
  //   same rules buildCouples uses for everyone else:
  //     1. Forward: viewer.spouseId points at a tiered live partner.
  //     2. Reverse: some live partner's spouseId points at the viewer.
  //   Either direction is enough (reciprocity is preferred but not
  //   required globally). The chosen partner must be in `live` (i.e.
  //   they have a tier assigned — Step 3b handles the case where the
  //   partner has no parents, by inheriting the viewer's tier 0).
  //
  //   This is what makes Chekesha's husband Terrance render side-by-side
  //   with her — with the heart connector — when Chekesha logs in and
  //   the tree centers on her.
  let youUnit: Unit | null = null;
  if (viewerId) {
    const youMember = byId.get(viewerId);
    if (youMember) {
      // Try couplePartner first (handles edge cases where both rows
      // are in `live` for some reason, e.g. shared-graph re-render).
      let spouse: GraphMember | null = null;
      const cpId = couplePartner.get(viewerId);
      if (cpId) {
        spouse = live.find((m) => m.id === cpId) ?? null;
      }
      // Fallback: read spouse_id directly off the viewer row + reverse
      // scan. This is the path that fires for the common case (viewer
      // is not in `live`, so couplePartner has no entry for them).
      if (!spouse) {
        // Forward: viewer.spouseId → live partner.
        const fwdId = youMember.spouseId ?? null;
        if (fwdId) {
          const cand = live.find((m) => m.id === fwdId);
          if (cand) spouse = cand;
        }
      }
      if (!spouse) {
        // Reverse: any live row whose spouseId points at the viewer.
        for (const m of live) {
          if (m.spouseId === viewerId) {
            spouse = m;
            break;
          }
        }
      }
      if (spouse) {
        // Couple. Order: YOU on left, spouse on right.
        youUnit = {
          id: `you+${spouse.id}`,
          members: [youMember, spouse],
          tier: 0,
          side: 'self',
          geometryRoot: 'self',
          lineageKey: 'self',
        };
        unitOfMember.set(viewerId, youUnit);
        unitOfMember.set(spouse.id, youUnit);
      } else {
        youUnit = {
          id: 'you',
          members: [youMember],
          tier: 0,
          side: 'self',
          geometryRoot: 'self',
          lineageKey: 'self',
        };
        unitOfMember.set(viewerId, youUnit);
      }
      allUnits.push(youUnit);
    }
  }

  // Fallback YOU unit (no viewerId): synthesized below.
  if (!youUnit) {
    youUnit = {
      id: 'you',
      members: [
        {
          id: '__you__',
          ownerUserId: '__you__',
          name: input.youName || 'You',
          relationship: 'unknown',
          side: 'self',
          approvalStatus: 'approved',
          confirmationsCount: 0,
          createdAt: new Date().toISOString(),
          profileState: 'placeholder',
          parentIds: [],
        } as GraphMember,
      ],
      tier: 0,
      side: 'self',
      geometryRoot: 'self',
      lineageKey: 'self',
    };
    allUnits.push(youUnit);
  }

  
// Build all other units.
  for (const m of live) {
    if (unitOfMember.has(m.id)) continue;
    const tier = tierById.get(m.id);
    if (tier === null || tier === undefined) continue;
    const partnerId = couplePartner.get(m.id);
    if (partnerId && !unitOfMember.has(partnerId)) {
      const partner = byId.get(partnerId);
      const partnerTier = partner
        ? tierById.get(partner.id) ?? null
        : null;
      // Couple only when both placeable on the SAME tier (otherwise the
      // pyramid would break — tiers are strict per-row).
      if (partner && partnerTier === tier) {
        const ordered =
          orderedCoupleMembers([m, partner]);

        const ownership =
          geometryOwnershipOf(ordered);

        const u: Unit = {
          id: `${ordered[0].id}+${ordered[1].id}`,
          members: ordered,
          tier,
          side: ownership.compatibilitySide,
          geometryRoot: ownership.root,
          lineageKey:
            ownership.layoutPath ??
            commonTreeBranchPrefix(
              ordered
                .map(
                  member =>
                    branchPathById.get(member.id),
                )
                .filter(
                  (value): value is string =>
                    !!value,
                ),
            ),
        };
        unitOfMember.set(ordered[0].id, u);
        unitOfMember.set(ordered[1].id, u);
        allUnits.push(u);
        continue;
      }
    }
    // Singleton.
    const ownership =
      geometryOwnershipOf([m]);

    const u: Unit = {
      id: m.id,
      members: [m],
      tier,
      side: ownership.compatibilitySide,
      geometryRoot: ownership.root,
      lineageKey:
        ownership.layoutPath ??
        branchPathById.get(m.id),
    };
    unitOfMember.set(m.id, u);
    allUnits.push(u);
  }

  // ─── Step 6: Deterministic per-member parent anchor ──────────────────
  //
  // Parent array order is NOT geometry authority.
  //
  // A child can have multiple canonical parent units:
  //
  //   • an explicit spouse/partner couple containing both parents;
  //   • a bloodline/viewer parent plus an independent co-parent;
  //   • multiple non-coupled parents in a blended family.
  //
  // Historical behavior selected the first usable `parentIds` entry.
  // That made the rendered graph change when the same parent set was
  // serialized in a different order.
  //
  // Re-derive candidate units as a SET and select deterministically.
  // Only a unit exactly one generation above the child is eligible.
  const parentUnitOfMember =
    new Map<string, Unit>();

  const parentAnchorRank = (
    unit: Unit,
  ): number => {
    if (
      viewerId &&
      unit.members.some(
        member =>
          member.id === viewerId,
      )
    ) {
      return 0;
    }

    let rank = 4;

    for (const member of unit.members) {
      const role =
        canonicalGeometryRoles.get(
          member.id,
        );

      if (!role) continue;

      if (
        !role.requiresCanonicalConnector
      ) {
        rank =
          Math.min(
            rank,
            1,
          );

        continue;
      }

      if (
        role.connectionKind ===
        'co-parent'
      ) {
        rank =
          Math.min(
            rank,
            2,
          );

        continue;
      }

      rank =
        Math.min(
          rank,
          3,
        );
    }

    return rank;
  };

  /*
   * Canonical root authority outranks opaque unit identity.
   *
   * Stable IDs may serialize a tie only after all structural
   * parent-anchor criteria are equivalent.
   */
  const parentAnchorRootRank:
    Readonly<
      Record<
        CanonicalTreeGeometryBranchRoot,
        number
      >
    > =
    Object.freeze({
      maternal:
        0,

      paternal:
        1,

      self:
        2,

      descendant:
        3,

      'child-mediated':
        4,

      both:
        5,

      'partner-affiliated':
        6,

      affiliated:
        7,

      unresolved:
        8,
    });

  const selectParentAnchorUnit = (
    member: GraphMember,
  ): Unit | null => {
    const memberTier =
      tierById.get(
        member.id,
      );

    if (
      memberTier === null ||
      memberTier === undefined
    ) {
      return null;
    }

    const parentIds =
      Array.from(
        new Set(
          (
            member.parentIds ??
            []
          ).filter(Boolean),
        ),
      );

    if (
      parentIds.length === 0
    ) {
      return null;
    }

    const parentIdSet =
      new Set(parentIds);

    const candidates:
      Unit[] = [];

    const seenUnitIds =
      new Set<string>();

    for (const parentId of parentIds) {
      const unit =
        unitOfMember.get(
          parentId,
        );

      if (
        !unit ||
        seenUnitIds.has(
          unit.id,
        )
      ) {
        continue;
      }

      /*
       * Fail closed on generation contradiction.
       * A parent anchor must be exactly one row above its child.
       */
      if (
        unit.tier !==
        memberTier + 1
      ) {
        continue;
      }

      seenUnitIds.add(
        unit.id,
      );

      candidates.push(
        unit,
      );
    }

    candidates.sort(
      (left, right) => {
        const leftCompleteCouple =
          left.members.length === 2 &&
          left.members.every(
            parent =>
              parentIdSet.has(
                parent.id,
              ),
          );

        const rightCompleteCouple =
          right.members.length === 2 &&
          right.members.every(
            parent =>
              parentIdSet.has(
                parent.id,
              ),
          );

        if (
          leftCompleteCouple !==
          rightCompleteCouple
        ) {
          return leftCompleteCouple
            ? -1
            : 1;
        }

        const rankDifference =
          parentAnchorRank(left) -
          parentAnchorRank(right);

        if (
          rankDifference !== 0
        ) {
          return rankDifference;
        }

        const rootRankDifference =
          parentAnchorRootRank[
            left.geometryRoot
          ] -
          parentAnchorRootRank[
            right.geometryRoot
          ];

        if (
          rootRankDifference !== 0
        ) {
          return rootRankDifference;
        }

        /*
         * Only structurally equivalent candidates may reach this
         * final stable serialization tie-break.
         */
        return left.id.localeCompare(
          right.id,
        );
      },
    );

    return (
      candidates[0] ??
      null
    );
  };

  for (const member of live) {
    const chosen =
      selectParentAnchorUnit(
        member,
      );

    if (chosen) {
      parentUnitOfMember.set(
        member.id,
        chosen,
      );
    }
  }

  /*
   * Viewer parent placement consumes the same deterministic authority.
   * No separate parent-array ordering rule exists for YOU.
   */
  if (
    viewerId &&
    anchor
  ) {
    const chosen =
      selectParentAnchorUnit(
        anchor,
      );

    if (chosen) {
      parentUnitOfMember.set(
        viewerId,
        chosen,
      );
    }
  }

  // ─── Step 7: Subtree-width memo (downward) ────────────────────────────
  // Each unit reserves horizontal space = max(its own width, sum of its
  // children's widths + gaps). Children of a unit = units whose anchor
  // parent unit IS this unit AND tier == this.tier - 1 (descendants only).
  const childrenOfUnit = new Map<string, Unit[]>();
  for (const u of allUnits) {
    for (const m of u.members) {
      // Find descendants: units at u.tier - 1 whose anchor parent is u.
      // We collect by scanning all units that have u as an anchor parent.
    }
  }
  // Build directly: scan all units to find those whose anchor parent is u
  // and that sit ONE TIER BELOW (i.e. descendants of u, not lateral siblings
  // / aunts which are children of u's parent).
  for (const u of allUnits) {
    for (const m of u.members) {
      const pu = parentUnitOfMember.get(m.id);
      if (!pu) continue;
      if (pu.tier - u.tier !== 1) continue;
      // u is one tier BELOW pu, and m's parent unit is pu. So u is a
      // descendant of pu.
      if (!childrenOfUnit.has(pu.id)) childrenOfUnit.set(pu.id, []);
      const arr = childrenOfUnit.get(pu.id)!;
      if (!arr.find((x) => x.id === u.id)) arr.push(u);
      break; // count u once per pu
    }
  }

  const subtreeWidth = new Map<string, number>();
  const computeWidth = (uId: string, seen: Set<string> = new Set()): number => {
    if (subtreeWidth.has(uId)) return subtreeWidth.get(uId)!;
    if (seen.has(uId)) return CARD_W; // cycle guard
    seen.add(uId);
    const u = allUnits.find((x) => x.id === uId);
    if (!u) return CARD_W;
    const own = widthOf(u);
    const kids = childrenOfUnit.get(uId) ?? [];
    if (kids.length === 0) {
      subtreeWidth.set(uId, own);
      return own;
    }
    let total = 0;
    for (const k of kids) total += computeWidth(k.id, seen);
    total +=
      LOCAL_FAMILY_GAP *
      Math.max(
        0,
        kids.length - 1,
      );
    const w = Math.max(own, total);
    subtreeWidth.set(uId, w);
    return w;
  };
  for (const u of allUnits) computeWidth(u.id);

  // ─── Step 8: Place YOU's unit at origin ───────────────────────────────
  const youY = 0;
  youUnit.x = 0;
  youUnit.y = youY;

  // ─── Step 9: Recursive descendant placement ───────────────────────────
  // Place all descendants of a given unit at tier - 1, centered under
  // its midpoint. Children's children recurse.
  const placeDescendants = (parent: Unit) => {
    const kids = childrenOfUnit.get(parent.id) ?? [];
    if (kids.length === 0) return;
    // Sort deterministically (by youngest member name for stability).
    const ordered = kids.slice().sort((a, b) => {
      const aIsDirectChild = a.members.some((m) => m.parentIds?.includes(viewerId ?? ''));
      const bIsDirectChild = b.members.some((m) => m.parentIds?.includes(viewerId ?? ''));
      if (aIsDirectChild !== bIsDirectChild) return aIsDirectChild ? -1 : 1;
      const an = a.members.map((m) => m.name).join('|');
      const bn = b.members.map((m) => m.name).join('|');
      return an.localeCompare(bn);
    });
    const widths = ordered.map((k) => computeWidth(k.id));
    const total =
      widths.reduce((s, w) => s + w, 0) +
      LOCAL_FAMILY_GAP *
        Math.max(
          0,
          ordered.length - 1,
        );
    /*
     * Center each descendant family beneath its actual parent.
     * Do not recursively add an extra global branch displacement at
     * every generation; that cumulative offset caused production spread.
     */
    let cursor =
      (parent.x ?? 0) -
      total / 2;
    const targetY = (parent.y ?? 0) + ROW_GAP;
    for (let i = 0; i < ordered.length; i++) {
      const k = ordered[i];
      const w = widths[i];
      k.x = cursor + w / 2;
      k.y = targetY;
      cursor += w + LOCAL_FAMILY_GAP;
    }
    for (const k of ordered) placeDescendants(k);
  };
  placeDescendants(youUnit);

  // ─── Step 10: Spine ancestor placement ────────────────────────────────
  // For each spine member (anchor + recursively their parents), place
  // their parent-unit centered at the member's x, one tier above. Then
  // place the parent-unit's OTHER children (= spine member's siblings)
  // alongside, on the OUTBOARD side relative to global center 0.
  //
  // Each placed sibling unit then gets its own descendants laid out below
  // (downward subtree placement). And each spine ancestor's own ancestors
  // recurse up.
  //
  // Critical: a spine member's x is already FIXED by the previous step
  // (e.g. mom is at couple-left of parents-unit). The parent-unit's
  // midpoint is anchored to that x.
  const placedUnitIds = new Set<string>();
  placedUnitIds.add(youUnit.id);
  // Tag every descendant unit we just placed.
  const collectPlaced = (u: Unit) => {
    placedUnitIds.add(u.id);
    for (const k of childrenOfUnit.get(u.id) ?? []) collectPlaced(k);
  };
  collectPlaced(youUnit);

  // ─── Step 9b: Descendant shared-parent placement ─────────────────────
  //
  // Recursive descendant placement follows one anchor parent unit so each
  // child occupies the graph only once. A visible second parent must still
  // receive a governed card and its own connector.
  //
  // For every already-placed descendant child with multiple distinct parent
  // units:
  //
  //   • preserve the selected anchor parent;
  //   • place each unplaced non-anchor parent on the same generation row;
  //   • retain singleton-unit ownership and therefore no spouse heart;
  //   • use the tighter co-parent cluster gap;
  //   • never move the viewer unit from the protected origin.
  //
  // Collision relaxation later retains the co-parent pair as a governed
  // cluster. Pure presentation only: no relationship data is changed.
  const promotedDescendantParentUnits:
    Unit[] =
      [];

  const placedDescendantChildren =
    live
      .filter(
        member => {
          const unit =
            unitOfMember.get(
              member.id,
            );

          return (
            unit?.x !== undefined &&
            unit?.y !== undefined &&
            (
              member.parentIds ??
              []
            ).filter(Boolean).length >
              1
          );
        },
      )
      .sort(
        (left, right) => {
          const leftTier =
            unitOfMember.get(
              left.id,
            )?.tier ??
            0;

          const rightTier =
            unitOfMember.get(
              right.id,
            )?.tier ??
            0;

          if (
            leftTier !==
            rightTier
          ) {
            return (
              rightTier -
              leftTier
            );
          }

          return left.id.localeCompare(
            right.id,
          );
        },
      );

  for (
    const child of
    placedDescendantChildren
  ) {
    const childUnit =
      unitOfMember.get(
        child.id,
      );

    const anchorParentUnit =
      parentUnitOfMember.get(
        child.id,
      );

    if (
      !childUnit ||
      childUnit.x === undefined ||
      childUnit.y === undefined ||
      !anchorParentUnit ||
      anchorParentUnit.x === undefined ||
      anchorParentUnit.y === undefined
    ) {
      continue;
    }

    const parentUnits:
      Unit[] =
        [];

    const seenParentUnitIds =
      new Set<string>();

    for (
      const parentId of
      (
        child.parentIds ??
        []
      ).filter(Boolean)
    ) {
      const parentUnit =
        unitOfMember.get(
          parentId,
        );

      if (
        !parentUnit ||
        seenParentUnitIds.has(
          parentUnit.id,
        )
      ) {
        continue;
      }

      seenParentUnitIds.add(
        parentUnit.id,
      );

      parentUnits.push(
        parentUnit,
      );
    }

    if (
      parentUnits.length <
      2
    ) {
      continue;
    }

    const secondaryParentUnits =
      parentUnits
        .filter(
          parentUnit =>
            parentUnit.id !==
              anchorParentUnit.id &&
            !placedUnitIds.has(
              parentUnit.id,
            ) &&
            parentUnit.tier ===
              childUnit.tier + 1,
        )
        .sort(
          (left, right) => {
            const sideRank = (
              unit:
                Unit,
            ) =>
              unit.side === 'mother'
                ? -1
                : unit.side === 'father'
                  ? 1
                  : 0;

            const sideDifference =
              sideRank(left) -
              sideRank(right);

            if (
              sideDifference !==
              0
            ) {
              return sideDifference;
            }

            return left.id.localeCompare(
              right.id,
            );
          },
        );

    let leftCursor =
      anchorParentUnit.x -
      widthOf(
        anchorParentUnit,
      ) /
        2 -
      CO_PARENT_CLUSTER_GAP;

    let rightCursor =
      anchorParentUnit.x +
      widthOf(
        anchorParentUnit,
      ) /
        2 +
      CO_PARENT_CLUSTER_GAP;

    let neutralIndex =
      0;

    for (
      const parentUnit of
      secondaryParentUnits
    ) {
      const width =
        widthOf(
          parentUnit,
        );

      const direction:
        -1 | 1 =
          parentUnit.side === 'mother'
            ? -1
            : parentUnit.side === 'father'
              ? 1
              : neutralIndex %
                    2 ===
                  0
                ? 1
                : -1;

      if (
        parentUnit.side !== 'mother' &&
        parentUnit.side !== 'father'
      ) {
        neutralIndex +=
          1;
      }

      if (
        direction ===
        -1
      ) {
        parentUnit.x =
          leftCursor -
          width /
            2;

        leftCursor -=
          width +
          CO_PARENT_CLUSTER_GAP;
      } else {
        parentUnit.x =
          rightCursor +
          width /
            2;

        rightCursor +=
          width +
          CO_PARENT_CLUSTER_GAP;
      }

      parentUnit.y =
        anchorParentUnit.y;

      placedUnitIds.add(
        parentUnit.id,
      );

      promotedDescendantParentUnits.push(
        parentUnit,
      );
    }
  }

  /**
   * Recursively place a spine member's parent-unit(s) and their lateral
   * descendants.
   *
   * SEPARATED-PARENTS RULE (bug fix — "after adding Dad while Mom already
   * exists, the tree collapses / Dad lands in Needs-connection overflow"):
   *
   * The previous version of this function only ever placed ONE parent
   * unit per spine member — the one returned by `parentUnitOfMember`,
   * which is keyed once-per-child. When a spine member has TWO parents
   * who are NOT linked via `spouse_id` (separated / never-coupled
   * co-parents), each parent lives in its OWN singleton unit. Only the
   * "first" one (per parent_ids ordering) was being placed; the other
   * never got an x/y, so:
   *   • Step 12 (units → cards) skipped it (no x defined),
   *   • Step 13 (overflow row) then dropped it into "Needs connection",
   *   • The visual tree appeared to collapse because Dad disappeared
   *     from the parents row and Mom (the chosen anchor) shifted to
   *     dead-center above YOU.
   *
   * The fix: gather ALL distinct parent units for the spine member and
   * place them side-by-side, centered above the spine member's x. Each
   * placed parent unit then independently:
   *   • places its lateral siblings (the spine member's aunts/uncles
   *     on that parent's side) outboard of itself, and
   *   • recurses up to its own ancestors (grandparents on that side).
   *
   * Pure layout — never mutates parent_ids, spouse_id, or any stored
   * relationship. The kinship engine, parent-edge connectors (step 15),
   * and secondary co-parent edges (step 15b) keep working unchanged.
   *
   * @param spineMember The member whose parents we want to place.
   * @param spineMemberX Fixed x of that member (already placed below).
   * @param spineMemberY Fixed y of that member.
   * @param outboardDir +1 = paternal-side (extends right); -1 = maternal-
   *                    side (extends left); 0 = mixed (split half/half).
   *                    Used as the fallback direction when a parent
   *                    unit's own side is 'self'/'both'.
   */
  const placeAncestorChain = (
    spineMember: GraphMember,
    spineMemberX: number,
    spineMemberY: number,
    outboardDir: -1 | 0 | 1,
  ) => {
    const pids = (spineMember.parentIds ?? []).filter(Boolean);
    if (pids.length === 0) return;

    // Collect the distinct parent units (couple unit counts once even if
    // both partners appear in pids).
    const parentUnits: Unit[] = [];
    const seenPu = new Set<string>();
    for (const pid of pids) {
      const pu = unitOfMember.get(pid);
      if (!pu) continue;
      if (seenPu.has(pu.id)) continue;
      seenPu.add(pu.id);
      parentUnits.push(pu);
    }
    if (parentUnits.length === 0) return;

    // Already-placed units stay where they are; we just need to (re)run
    // their lateral-sibling + ancestor recursion the first time we visit
    // them. Filter to the units that still need positioning.
    const needsPlacement = parentUnits.filter(
      (pu) => !placedUnitIds.has(pu.id),
    );
    if (needsPlacement.length === 0) return;

    // ── Position the parent units that still need placement ──
    // Order: maternal-side first (extends left), self/both in middle,
    // paternal-side last (extends right). Stable secondary by name.
    const branchDir = outboardDir;
      const sideRank = (pu: Unit) =>
        branchDir !== 0 ? branchDir : pu.side === 'mother' ? -1 : pu.side === 'father' ? 1 : 0;
    needsPlacement.sort((a, b) => {
      const dr = sideRank(a) - sideRank(b);
      if (dr !== 0) return dr;
      const an = a.members.map((m) => m.name).join('|');
      const bn = b.members.map((m) => m.name).join('|');
      return an.localeCompare(bn);
    });

    const groupY = spineMemberY - ROW_GAP;
    if (needsPlacement.length === 1) {
      // Single parent unit — center directly above the spine member.
      const pu = needsPlacement[0];
      const dir = pu.side === 'mother' ? -1 : pu.side === 'father' ? 1 : outboardDir;
      pu.x =
        spineMemberX +
        (
          dir === 0
            ? 0
            : dir *
              (
                widthOf(pu) / 2 +
                LOCAL_FAMILY_GAP / 2
              )
        );
      pu.y = groupY;
      placedUnitIds.add(pu.id);
    } else {
      // Multiple non-coupled parent units (separated co-parents / blended-
      // family co-parents / never-spouse-linked parents that nonetheless
      // share this child via parent_ids).
      //
      // PARENT-PAIR CLUSTERING (global layout spec):
      //   The two (or more) units form a "co-parent cluster" centered
      //   above the spine member. Per spec: "Render that parent pair
      //   together above the shared child. Their spacing should be CLOSER
      //   to each other than to siblings / aunts / uncles on the same
      //   generation row." We therefore use the dedicated
      //   `CO_PARENT_CLUSTER_GAP` between sibling co-parent units instead
      //   of the much-wider `BRANCH_GAP`. The heart connector is NOT
      //   drawn (spouse_id is unset / not reciprocated — buildCouples
      //   already ruled out a couple unit), so visually the two cards
      //   read as a tight parent pair without implying a romantic
      //   relationship. Pure layout — no spouse_id is ever written,
      //   no parent_ids are ever rewritten.
      const widths = needsPlacement.map(widthOf);
      const totalW =
        widths.reduce((s, w) => s + w, 0) +
        CO_PARENT_CLUSTER_GAP * Math.max(0, needsPlacement.length - 1);
      let cursor = spineMemberX - totalW / 2;
      for (let i = 0; i < needsPlacement.length; i++) {
        const pu = needsPlacement[i];
        const w = widths[i];
        pu.x = cursor + w / 2;
        pu.y = groupY;
        placedUnitIds.add(pu.id);
        cursor += w + CO_PARENT_CLUSTER_GAP;
      }
    }


    // ── For every parent unit (newly placed OR pre-existing), place its
    //    lateral siblings outboard AND recurse up. We iterate the FULL
    //    parentUnits list (not just needsPlacement) so the recursion
    //    drives every branch up to the canvas top, but the
    //    `placedUnitIds.has(...)` guards inside ensure we never clobber
    //    an already-placed sibling unit. ──
    for (const pu of parentUnits) {
      const puDir: -1 | 0 | 1 =
        branchDir !== 0 ? branchDir : pu.side === 'mother' ? -1 : pu.side === 'father' ? 1 : outboardDir;

      // Lateral siblings of spineMember via this parent unit.
      const siblings = (childrenOfUnit.get(pu.id) ?? []).filter(
        (k) => !k.members.some((m) => m.id === spineMember.id),
      );
      if (siblings.length > 0) {
        const ordered = siblings.slice().sort((a, b) => {
          const an = a.members.map((m) => m.name).join('|');
          const bn = b.members.map((m) => m.name).join('|');
          return an.localeCompare(bn);
        });
        const widths = ordered.map((k) => computeWidth(k.id));
        const puHalf = widthOf(pu) / 2;
        const puX = pu.x ?? 0;

        if (puDir === -1) {
          // Maternal — siblings extend LEFT of the parent unit.
          let cursor = puX - puHalf - LOCAL_FAMILY_GAP;
          for (let i = 0; i < ordered.length; i++) {
            const k = ordered[i];
            const w = widths[i];
            if (!placedUnitIds.has(k.id)) {
              k.x = cursor - w / 2;
              k.y = spineMemberY;
              placedUnitIds.add(k.id);
            }
            cursor -= w + LOCAL_FAMILY_GAP;
          }
        } else if (puDir === 1) {
          let cursor = puX + puHalf + LOCAL_FAMILY_GAP;
          for (let i = 0; i < ordered.length; i++) {
            const k = ordered[i];
            const w = widths[i];
            if (!placedUnitIds.has(k.id)) {
              k.x = cursor + w / 2;
              k.y = spineMemberY;
              placedUnitIds.add(k.id);
            }
            cursor += w + LOCAL_FAMILY_GAP;
          }
        } else {
          // Mixed — split half/half around the parent unit.
          const half = Math.ceil(ordered.length / 2);
          const left = ordered.slice(0, half);
          const right = ordered.slice(half);
          let lCursor = puX - puHalf - LOCAL_FAMILY_GAP;
          for (let i = 0; i < left.length; i++) {
            const k = left[i];
            const w = widths[ordered.indexOf(k)];
            if (!placedUnitIds.has(k.id)) {
              k.x = lCursor - w / 2;
              k.y = spineMemberY;
              placedUnitIds.add(k.id);
            }
            lCursor -= w + LOCAL_FAMILY_GAP;
          }
          let rCursor = puX + puHalf + LOCAL_FAMILY_GAP;
          for (let i = 0; i < right.length; i++) {
            const k = right[i];
            const w = widths[ordered.indexOf(k)];
            if (!placedUnitIds.has(k.id)) {
              k.x = rCursor + w / 2;
              k.y = spineMemberY;
              placedUnitIds.add(k.id);
            }
            rCursor += w + LOCAL_FAMILY_GAP;
          }
        }
        // Lateral siblings get their own descendant subtrees.
        for (const k of ordered) placeDescendants(k);
      }

      // Recurse UP through each member of this parent unit. For couple
      // units, the left member extends maternal (-1), right paternal
      // (+1). Singleton parent units inherit the parent unit's own
      // direction.
      const orderedCouple = orderedCoupleMembers(pu.members);
      if (orderedCouple.length === 2) {
        const left = orderedCouple[0];
        const right = orderedCouple[1];
        const leftX = (pu.x ?? 0) - (CARD_W + COUPLE_GAP) / 2;
        const rightX = (pu.x ?? 0) + (CARD_W + COUPLE_GAP) / 2;
        placeAncestorChain(left, leftX, pu.y ?? 0, puDir);
        placeAncestorChain(right, rightX, pu.y ?? 0, puDir);
      } else {
        placeAncestorChain(orderedCouple[0], pu.x ?? 0, pu.y ?? 0, puDir);
      }
    }
  };

  // YOU's spine: kick off the ancestor chain. The viewer's parent
  // unit(s) sit centered above YOU. When YOU has multiple non-coupled
  // co-parents (e.g. separated mom + dad), they're placed side-by-side
  // by the multi-parent branch above — mom ends up on the left, dad on
  // the right, neither on dead-center, so the tree no longer compresses
  // and Dad never falls into the "Needs connection" overflow row.
  if (viewerId) {
    const youMember = byId.get(viewerId);
    if (youMember) {
      placeAncestorChain(
        youMember,
        youUnit.x ?? 0,
        youUnit.y ?? 0,
        0, // YOU's siblings split half-half.
      );
    }
  }

  // Promoted descendant co-parents may own their own visible ancestry.
  // Drive the same ancestor-chain authority from each promoted parent
  // without changing its already governed card coordinates.
  for (
    const promotedParentUnit of
    promotedDescendantParentUnits
  ) {
    const orderedPromotedMembers =
      orderedCoupleMembers(
        promotedParentUnit.members,
      );

    for (
      const promotedParent of
      orderedPromotedMembers
    ) {
      placeAncestorChain(
        promotedParent,
        promotedParentUnit.x ??
          0,
        promotedParentUnit.y ??
          0,
        promotedParentUnit.side === 'mother'
          ? -1
          : promotedParentUnit.side === 'father'
            ? 1
            : 0,
      );
    }
  }


  // ─── Step 11: Per-tier collision relaxation ───────────────────────────
  // Some pathological graphs (multiple cousin branches, very dense
  // ancestor lines) might still produce overlapping units on the same
  // tier. Run a simple left-to-right relaxation to enforce min spacing.
  //
  // PARENT-PAIR CLUSTER PRESERVATION (global layout spec):
  //   Two units that are CO-PARENTS of the same child (both contain at
  //   least one member listed in some child's `parent_ids`) must be
  //   treated as a single visual cluster: they may sit as close as
  //   `CO_PARENT_CLUSTER_GAP` to each other and should NOT be pulled
  //   apart toward `CLUSTER_GAP` by the relaxation pass. Without this
  //   guard the relaxation would shove two non-coupled co-parents (no
  //   spouse_id) outward whenever any other same-tier unit needs room
  //   on the row — visually separating the parent pair from each other
  //   and from their shared child below.
  //
  // FAMILY-SIDE LANE GUARD (global layout spec):
  //   Mother-side cards must remain in the LEFT lane (x < YOU.x) and
  //   father-side cards must remain in the RIGHT lane (x > YOU.x) on
  //   ancestor / sibling tiers. When relaxation would otherwise push a
  //   mother-side card to the right of YOU's center (or a father-side
  //   card to the left), we shove the OTHER unit instead so the lane
  //   boundary is preserved. Pure presentation guard — no row's stored
  //   `side` value is ever changed. (Descendant tier 0/-1/-2 children
  //   are 'self' and unaffected by this guard.)
  //
  // Build the co-parent pair set: any two units that contribute to the
  // same child's parent_ids are flagged. Single pass over `live`.
  const coParentPairKey = new Set<string>();
  const pairKey = (idA: string, idB: string) =>
    idA < idB ? `${idA}::${idB}` : `${idB}::${idA}`;
  for (const m of live) {
    const pids = (m.parentIds ?? []).filter(Boolean);
    if (pids.length < 2) continue;
    const unitIds: string[] = [];
    const seenU = new Set<string>();
    for (const pid of pids) {
      const pu = unitOfMember.get(pid);
      if (!pu) continue;
      if (seenU.has(pu.id)) continue;
      seenU.add(pu.id);
      unitIds.push(pu.id);
    }
    for (let i = 0; i < unitIds.length; i++) {
      for (let j = i + 1; j < unitIds.length; j++) {
        coParentPairKey.add(pairKey(unitIds[i], unitIds[j]));
      }
    }
  }
  const youX0 = youUnit.x ?? 0;
  // Tiny epsilon so we never refuse a shove for floating-point ties.
  const LANE_EPSILON = 0.5;

  const tiersPresent = new Set<number>();
  for (const u of allUnits) {
    if (u.x !== undefined) tiersPresent.add(u.tier);
  }
  for (const t of tiersPresent) {
    const row = allUnits
      .filter((u) => u.tier === t && u.x !== undefined)
      .sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (let i = 1; i < row.length; i++) {
        const a = row[i - 1];
        const b = row[i];
        const aRight = (a.x ?? 0) + widthOf(a) / 2;
        const bLeft = (b.x ?? 0) - widthOf(b) / 2;
        const gap = bLeft - aRight;
        // Co-parent pairs: tolerate the much tighter CO_PARENT_CLUSTER_GAP
        // instead of the full CLUSTER_GAP. They stay clustered together.
        const isCoParentPair = coParentPairKey.has(pairKey(a.id, b.id));
        const requiredGap =
          isCoParentPair
            ? CO_PARENT_CLUSTER_GAP
            : sameCanonicalFamilyCluster(
                a,
                b,
              )
              ? LOCAL_FAMILY_GAP
              : FAMILY_CLUSTER_GAP;
        if (gap < requiredGap) {
          const shove = requiredGap - gap;
          // Lane-aware shove: never push a mother-side ancestor across
          // YOU's center axis to the right, and never push a father-side
          // ancestor across to the left. (Tier 0 / descendant tiers are
          // exempted — those rows are YOU's own row or below and use the
          // standard symmetric split.)
          const aSide = a.side;
          const bSide = b.side;
          const isProtectedFamilyTier = t >= 0;
          const aIsYou = a.id === youUnit.id;
          const bIsYou = b.id === youUnit.id;

          // Default: symmetric split.
          let pushAOnly = false;
          let pushBOnly = false;

          if (aIsYou) {
            pushBOnly = true; // YOU stays put.
          } else if (bIsYou) {
            pushAOnly = true; // YOU stays put.
          } else if (isProtectedFamilyTier) {
            // Lane guard — only constrain ancestor tier.
            //
            // Use the UNIT CENTER (not its edge) to decide whether the
            // unit is "in" the maternal/paternal lane: a parents-couple
            // unit legitimately straddles YOU (mom-half left, dad-half
            // right) with its center at YOU's center, so an edge-based
            // check would mis-fire on the normal parents row. The lane
            // boundary is therefore "unit center must stay on the
            // correct side of YOU's center".
            const aPredictedCenter = (a.x ?? 0) - shove / 2;
            const bPredictedCenter = (b.x ?? 0) + shove / 2;
            // Mother-side `a`'s center must stay at or left of YOU.x.
            if (
              aSide === 'mother' &&
              aPredictedCenter > youX0 + LANE_EPSILON
            ) {
              pushBOnly = true;
            }
            // Father-side `b`'s center must stay at or right of YOU.x.
            else if (
              bSide === 'father' &&
              bPredictedCenter < youX0 - LANE_EPSILON
            ) {
              pushAOnly = true;
            }
          }

          if (pushAOnly) {
            a.x = (a.x ?? 0) - shove;
          } else if (pushBOnly) {
            b.x = (b.x ?? 0) + shove;
          } else {
            a.x = (a.x ?? 0) - shove / 2;
            b.x = (b.x ?? 0) + shove / 2;
          }
          moved = true;
        }
      }
      if (!moved) break;
    }
  }


  // ─── Step 11b: Recursive branch-interval reservation ────────────────
  //
  // Every recursive ancestry path owns one contiguous horizontal interval.
  // Sibling branch intervals are laid out sequentially and may expand only
  // horizontally. Growth in one branch pushes the next branch outward.
  //
  // Generation rows are immutable: this pass changes x only, never y or tier.
  const branchUnitsForPrefix = (
    prefix: string,
    root: CanonicalTreeGeometryAncestryRoot,
  ): Unit[] =>
    allUnits.filter(
      (unit) =>
        unit.geometryRoot === root &&
        unit.x !== undefined &&
        !!unit.lineageKey &&
        (
          unit.lineageKey === prefix ||
          unit.lineageKey.startsWith(`${prefix}.`)
        ),
    );

  const placedUnitsForGeometryRoot = (
    root: CanonicalTreeGeometryAncestryRoot,
  ): Unit[] =>
    allUnits.filter(
      (unit) =>
        unit.geometryRoot === root &&
        unit.x !== undefined,
    );

  const branchBounds = (
    units: Unit[],
  ): { min: number; max: number; width: number; center: number } | null => {
    if (units.length === 0) return null;

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    for (const unit of units) {
      const center = unit.x ?? 0;
      const half = widthOf(unit) / 2;
      min = Math.min(min, center - half);
      max = Math.max(max, center + half);
    }

    return {
      min,
      max,
      width: max - min,
      center: (min + max) / 2,
    };
  };

  const shiftBranchUnits = (
    units: Unit[],
    deltaX: number,
  ) => {
    if (!Number.isFinite(deltaX) || Math.abs(deltaX) < 0.001) return;

    for (const unit of units) {
      if (unit.x === undefined) continue;
      unit.x += deltaX;
    }
  };

  const immediateNumericChildPrefixes = (
    parentPrefix: string,
    root: CanonicalTreeGeometryAncestryRoot,
  ): string[] => {
    const prefixes = new Set<string>();

    for (const unit of allUnits) {
      if (unit.geometryRoot !== root) continue;

      const path = unit.lineageKey;
      if (!path || !path.startsWith('ancestor.')) continue;

      const parts = path.split('.');

      const parentParts = parentPrefix.split('.');

      if (
        parts.length <= parentParts.length ||
        parts.slice(0, parentParts.length).join('.') !== parentPrefix
      ) {
        continue;
      }

      const next = parts[parentParts.length];

      if (!/^\d+$/.test(next)) continue;

      prefixes.add(
        [...parentParts, next].join('.'),
      );
    }

    return Array.from(prefixes).sort((left, right) => {
      const leftToken = Number(left.split('.').at(-1));
      const rightToken = Number(right.split('.').at(-1));
      return leftToken - rightToken;
    });
  };

  const reserveBranchIntervals = (
    parentPrefix: string,
    preferredCenter: number,
    root: CanonicalTreeGeometryAncestryRoot,
    visited: Set<string> = new Set(),
  ) => {
    const visitKey =
      `${root}:${parentPrefix}`;

    if (visited.has(visitKey)) return;
    visited.add(visitKey);

    const childPrefixes =
      immediateNumericChildPrefixes(
        parentPrefix,
        root,
      );

    const groups = childPrefixes
      .map((prefix) => {
        const units =
          branchUnitsForPrefix(
            prefix,
            root,
          );
        const bounds = branchBounds(units);
        return bounds ? { prefix, units, bounds } : null;
      })
      .filter(
        (
          group,
        ): group is {
          prefix: string;
          units: Unit[];
          bounds: {
            min: number;
            max: number;
            width: number;
            center: number;
          };
        } => !!group,
      );

    if (groups.length === 0) return;

    const totalWidth =
      groups.reduce(
        (sum, group) => sum + group.bounds.width,
        0,
      ) +
      FAMILY_CLUSTER_GAP *
        Math.max(
          0,
          groups.length - 1,
        );

    let cursor = preferredCenter - totalWidth / 2;

    for (const group of groups) {
      const targetCenter =
        cursor + group.bounds.width / 2;

      shiftBranchUnits(
        group.units,
        targetCenter - group.bounds.center,
      );

      cursor +=
        group.bounds.width +
        FAMILY_CLUSTER_GAP;
    }

    for (const group of groups) {
      const updatedBounds =
        branchBounds(
          branchUnitsForPrefix(
            group.prefix,
            root,
          ),
        );

      reserveBranchIntervals(
        group.prefix,
        updatedBounds?.center ?? preferredCenter,
        root,
        visited,
      );
    }
  };

  const maternalRootUnits =
    placedUnitsForGeometryRoot(
      'maternal',
    );

  const paternalRootUnits =
    placedUnitsForGeometryRoot(
      'paternal',
    );

  const maternalRootBounds =
    branchBounds(
      maternalRootUnits,
    );

  const paternalRootBounds =
    branchBounds(
      paternalRootUnits,
    );

  reserveBranchIntervals(
    'ancestor.0',
    maternalRootBounds?.center ??
      (youUnit.x ?? 0),
    'maternal',
  );

  reserveBranchIntervals(
    'ancestor.1',
    paternalRootBounds?.center ??
      (youUnit.x ?? 0),
    'paternal',
  );

  const rootEnvelopePlan =
    planCanonicalTreeGeometryRootEnvelopes({
      units:
        allUnits
          .filter(
            unit =>
              unit.x !== undefined,
          )
          .map(
            unit => ({
              unitId:
                unit.id,

              root:
                unit.geometryRoot,

              x:
                unit.x ?? 0,

              width:
                widthOf(unit),
            }),
          ),

      viewerCenterX:
        youUnit.x ?? 0,

      viewerEnvelopeWidth:
        widthOf(youUnit),

      rootGap:
        ROOT_BOUNDARY_GAP,
    });

  for (const unit of allUnits) {
    if (unit.x === undefined) continue;

    const plannedX =
      rootEnvelopePlan.plannedXByUnitId.get(
        unit.id,
      );

    if (plannedX === undefined) {
      throw new Error(
        `Missing root-envelope position for unit ${unit.id}.`,
      );
    }

    unit.x =
      plannedX;
  }

  /*
   * CC-TREE-LIVE-003G3R
   *
   * POST-ENVELOPE LATERAL-SIBLING CONTAINMENT
   *
   * Recursive branch reservation and root-envelope planning execute after
   * the general collision pass. They may therefore move a structurally
   * valid lateral sibling back across its ancestry-spine member.
   *
   * Reassert only the canonical spine/lateral ordering:
   *
   *   maternal lateral sibling -> outboard LEFT
   *   paternal lateral sibling -> outboard RIGHT
   *
   * Move the lateral member's complete lineage subtree with it so its
   * descendants remain clustered. Movement is exclusively away from the
   * viewer axis and therefore cannot weaken the protected root boundary.
   */
  const canonicalLateralSiblingSpineKey = (
    lineageKey:
      string | null | undefined,
  ): string | null => {
    const normalized =
      (lineageKey ?? '').trim();

    const match =
      /^(ancestor(?:\.\d+)+)\.child\.\d+(?:\.partner)?$/.exec(
        normalized,
      );

    if (!match) {
      return null;
    }

    const parentPath =
      match[1].split('.');

    /*
     * ancestor.1.0.child.1
     *          ^^^^^^^^
     * siblings share ancestor.1.0 as their parent branch;
     * the corresponding ancestry spine member is ancestor.1.
     */
    if (parentPath.length < 3) {
      return null;
    }

    parentPath.pop();

    return parentPath.join('.');
  };

  const spineUnitByGeometryKey =
    new Map<string, Unit>();

  for (const unit of allUnits) {
    if (
      unit.x === undefined ||
      !unit.lineageKey ||
      (
        unit.geometryRoot !==
          'maternal' &&
        unit.geometryRoot !==
          'paternal'
      )
    ) {
      continue;
    }

    if (
      unit.lineageKey.includes(
        '.child.',
      )
    ) {
      continue;
    }

    spineUnitByGeometryKey.set(
      [
        unit.geometryRoot,
        unit.tier,
        unit.lineageKey,
      ].join(':'),
      unit,
    );
  }

  for (const lateralUnit of allUnits) {
    if (
      lateralUnit.x === undefined ||
      !lateralUnit.lineageKey ||
      (
        lateralUnit.geometryRoot !==
          'maternal' &&
        lateralUnit.geometryRoot !==
          'paternal'
      )
    ) {
      continue;
    }

    const spineKey =
      canonicalLateralSiblingSpineKey(
        lateralUnit.lineageKey,
      );

    if (!spineKey) {
      continue;
    }

    const spineUnit =
      spineUnitByGeometryKey.get(
        [
          lateralUnit.geometryRoot,
          lateralUnit.tier,
          spineKey,
        ].join(':'),
      );

    if (
      !spineUnit ||
      spineUnit.x === undefined
    ) {
      continue;
    }

    const requiredCenterDistance =
      widthOf(spineUnit) / 2 +
      LOCAL_FAMILY_GAP +
      widthOf(lateralUnit) / 2;

    let deltaX = 0;

    if (
      lateralUnit.geometryRoot ===
      'paternal'
    ) {
      const minimumLateralX =
        spineUnit.x +
        requiredCenterDistance;

      if (
        lateralUnit.x <
        minimumLateralX
      ) {
        deltaX =
          minimumLateralX -
          lateralUnit.x;
      }
    } else {
      const maximumLateralX =
        spineUnit.x -
        requiredCenterDistance;

      if (
        lateralUnit.x >
        maximumLateralX
      ) {
        deltaX =
          maximumLateralX -
          lateralUnit.x;
      }
    }

    if (
      Math.abs(deltaX) <
      0.001
    ) {
      continue;
    }

    const lateralBranchUnits =
      branchUnitsForPrefix(
        lateralUnit.lineageKey,
        lateralUnit.geometryRoot,
      );

    shiftBranchUnits(
      lateralBranchUnits.length > 0
        ? lateralBranchUnits
        : [lateralUnit],
      deltaX,
    );
  }

  // ─── LIVE-002D: Post-reservation protected viewer envelope ────────────
  // Recursive branch reservation runs after the general collision pass and
  // may move same-tier units back toward the viewer. Re-enforce the viewer
  // row after all branch shifts are complete.
  //
  // The viewer unit remains fixed. Separate co-parents retain the tighter
  // governed co-parent gap and remain non-spouse units. Every other adjacent
  // unit receives the normal cluster gap.
  if (
    viewerId &&
    youUnit.x !== undefined &&
    youUnit.y !== undefined
  ) {
    const viewerTier = youUnit.tier;

    const viewerRow = allUnits
      .filter(
        (unit) =>
          unit.tier === viewerTier &&
          unit.x !== undefined,
      )
      .sort(
        (left, right) =>
          (left.x ?? 0) - (right.x ?? 0),
      );

    const viewerIndex = viewerRow.findIndex(
      (unit) => unit.id === youUnit.id,
    );

    if (viewerIndex >= 0) {
      const requiredUnitGap = (
        left: Unit,
        right: Unit,
      ): number =>
        coParentPairKey.has(
          pairKey(left.id, right.id),
        )
          ? CO_PARENT_CLUSTER_GAP
          : sameCanonicalFamilyCluster(
              left,
              right,
            )
            ? LOCAL_FAMILY_GAP
            : FAMILY_CLUSTER_GAP;

      for (
        let index = viewerIndex - 1;
        index >= 0;
        index -= 1
      ) {
        const left = viewerRow[index];
        const right = viewerRow[index + 1];

        const maximumLeftCenter =
          (right.x ?? 0) -
          widthOf(right) / 2 -
          requiredUnitGap(left, right) -
          widthOf(left) / 2;

        if ((left.x ?? 0) > maximumLeftCenter) {
          left.x = maximumLeftCenter;
        }
      }

      for (
        let index = viewerIndex + 1;
        index < viewerRow.length;
        index += 1
      ) {
        const left = viewerRow[index - 1];
        const right = viewerRow[index];

        const minimumRightCenter =
          (left.x ?? 0) +
          widthOf(left) / 2 +
          requiredUnitGap(left, right) +
          widthOf(right) / 2;

        if ((right.x ?? 0) < minimumRightCenter) {
          right.x = minimumRightCenter;
        }
      }
    }
  }

  // ─── F2B2B2: Canonical modern-family unit placement ──────────────────
  // Canonically reachable nonancestral members own a real generation tier
  // even when legacy parent/spouse topology cannot attach them to the
  // ancestry pyramid. Place those units on their governed row at a stable
  // horizontal edge. This is presentation-only and never rewrites topology.
  const canonicalRoleUnits =
    allUnits
      .filter(
        unit =>
          unit.x === undefined &&
          unit.members.some(member =>
            canonicalGeometryRoles.has(
              member.id,
            ),
          ),
      )
      .sort((left, right) => {
        if (left.tier !== right.tier) {
          return right.tier - left.tier;
        }

        return left.id.localeCompare(
          right.id,
        );
      });

  const placedUnitsOnTier = (
    tier: number,
  ): Unit[] =>
    allUnits.filter(
      unit =>
        unit.tier === tier &&
        unit.x !== undefined,
    );

  for (const unit of canonicalRoleUnits) {
    const primaryMember =
      unit.members[0];

    const role =
      canonicalGeometryRoles.get(
        primaryMember.id,
      );

    if (!role) continue;

    const anchorUnit =
      role.anchorMemberId
        ? unitOfMember.get(
            role.anchorMemberId,
          ) ?? null
        : null;

    const row =
      placedUnitsOnTier(
        unit.tier,
      );

    const rowMin =
      row.length > 0
        ? Math.min(
            ...row.map(
              placed =>
                (placed.x ?? 0) -
                widthOf(placed) / 2,
            ),
          )
        : -widthOf(youUnit) / 2;

    const rowMax =
      row.length > 0
        ? Math.max(
            ...row.map(
              placed =>
                (placed.x ?? 0) +
                widthOf(placed) / 2,
            ),
          )
        : widthOf(youUnit) / 2;

    const anchorX =
      anchorUnit?.x ??
      youUnit.x ??
      0;

    const placeLeft =
      role.side === 'mother' ||
      (
        role.side !== 'father' &&
        anchorX < (youUnit.x ?? 0)
      );

    unit.x =
      placeLeft
        ? rowMin -
          CLUSTER_GAP -
          widthOf(unit) / 2
        : rowMax +
          CLUSTER_GAP +
          widthOf(unit) / 2;

    unit.y =
      -unit.tier * ROW_GAP;

    placedUnitIds.add(
      unit.id,
    );
  }

  // Constitutional invariant: recursive interval reservation is horizontal
  // only. Every placed unit must still sit on its tier's exact generation row.
  for (const unit of allUnits) {
    if (unit.y === undefined) continue;

    const expectedY = -unit.tier * ROW_GAP;

    if (Math.abs(unit.y - expectedY) > 0.001) {
      unit.y = expectedY;
    }
  }

  // ─── LIVE-002D: Actual viewer-card origin authority ───────────────────
  // The viewer unit midpoint is not necessarily the viewer card center
  // when the viewer shares a spouse/partner couple unit. Normalize every
  // placed unit together so the actual viewer card owns x=0 and y=0.
  // Pure presentation: no member or relationship data is mutated.
  if (viewerId && !centeredMemberId) {
    const orderedViewerUnit =
      orderedCoupleMembers(youUnit.members);

    const viewerIndex =
      orderedViewerUnit.findIndex(
        (member) => member.id === viewerId,
      );

    const viewerOffsetX =
      orderedViewerUnit.length === 2
        ? viewerIndex === 0
          ? -(CARD_W + COUPLE_GAP) / 2
          : (CARD_W + COUPLE_GAP) / 2
        : 0;

    const viewerCardX =
      (youUnit.x ?? 0) + viewerOffsetX;

    const viewerCardY =
      youUnit.y ?? 0;

    for (const unit of allUnits) {
      if (unit.x !== undefined) {
        unit.x -= viewerCardX;
      }

      if (unit.y !== undefined) {
        unit.y -= viewerCardY;
      }
    }
  }

  /*
   * CC-TREE-LIVE-003G3R
   *
   * FINAL SAME-TIER COLLISION CLOSURE
   *
   * Earlier collision relaxation cannot be the final authority because
   * recursive branch reservation, root-envelope enforcement, and canonical
   * role placement may all change x-coordinates afterward.
   *
   * This pass runs only after every placement authority has finished and
   * before units become cards.
   *
   * Safety:
   *   - viewer unit never moves;
   *   - maternal ancestry may move outward left only;
   *   - paternal ancestry may move outward right only;
   *   - co-parent pair gap remains governed separately;
   *   - same canonical family cluster uses LOCAL_FAMILY_GAP;
   *   - separate family clusters use FAMILY_CLUSTER_GAP;
   *   - y/tier/topology/member data are immutable.
   */
  const finalRequiredUnitGap = (
    left: Unit,
    right: Unit,
  ): number =>
    coParentPairKey.has(
      pairKey(left.id, right.id),
    )
      ? CO_PARENT_CLUSTER_GAP
      : sameCanonicalFamilyCluster(
          left,
          right,
        )
        ? LOCAL_FAMILY_GAP
        : FAMILY_CLUSTER_GAP;

  const finalPlacedTiers =
    Array.from(
      new Set(
        allUnits
          .filter(
            unit =>
              unit.x !== undefined &&
              unit.y !== undefined,
          )
          .map(
            unit =>
              unit.tier,
          ),
      ),
    ).sort(
      (left, right) =>
        right - left,
    );

  for (
    const tier of
    finalPlacedTiers
  ) {
    const row =
      allUnits
        .filter(
          unit =>
            unit.tier === tier &&
            unit.x !== undefined &&
            unit.y !== undefined,
        );

    const leftOfViewer =
      row
        .filter(
          unit =>
            unit.id !== youUnit.id &&
            (
              unit.geometryRoot ===
                'maternal' ||
              (
                unit.geometryRoot !==
                  'paternal' &&
                (unit.x ?? 0) <
                  (youUnit.x ?? 0)
              )
            ),
        )
        .sort(
          (left, right) => {
            const dx =
              (right.x ?? 0) -
              (left.x ?? 0);

            return (
              Math.abs(dx) > 0.001
                ? dx
                : right.id.localeCompare(
                    left.id,
                  )
            );
          },
        );

    let innerLeft:
      Unit | null =
        row.includes(youUnit)
          ? youUnit
          : null;

    for (
      const unit of
      leftOfViewer
    ) {
      if (innerLeft) {
        const maximumCenter =
          (innerLeft.x ?? 0) -
          widthOf(innerLeft) / 2 -
          finalRequiredUnitGap(
            unit,
            innerLeft,
          ) -
          widthOf(unit) / 2;

        if (
          (unit.x ?? 0) >
          maximumCenter
        ) {
          unit.x =
            maximumCenter;
        }
      }

      innerLeft = unit;
    }

    const rightOfViewer =
      row
        .filter(
          unit =>
            unit.id !== youUnit.id &&
            (
              unit.geometryRoot ===
                'paternal' ||
              (
                unit.geometryRoot !==
                  'maternal' &&
                (unit.x ?? 0) >=
                  (youUnit.x ?? 0)
              )
            ),
        )
        .sort(
          (left, right) => {
            const dx =
              (left.x ?? 0) -
              (right.x ?? 0);

            return (
              Math.abs(dx) > 0.001
                ? dx
                : left.id.localeCompare(
                    right.id,
                  )
            );
          },
        );

    let innerRight:
      Unit | null =
        row.includes(youUnit)
          ? youUnit
          : null;

    for (
      const unit of
      rightOfViewer
    ) {
      if (innerRight) {
        const minimumCenter =
          (innerRight.x ?? 0) +
          widthOf(innerRight) / 2 +
          finalRequiredUnitGap(
            innerRight,
            unit,
          ) +
          widthOf(unit) / 2;

        if (
          (unit.x ?? 0) <
          minimumCenter
        ) {
          unit.x =
            minimumCenter;
        }
      }

      innerRight = unit;
    }
  }

  // ─── Step 12: Convert units → cards ───────────────────────────────────
  const cards: PyramidCard[] = [];
  const cardPosByMemberId = new Map<string, { x: number; y: number; side: SideKey; isYou?: boolean }>();
  for (const u of allUnits) {
    if (u.x === undefined || u.y === undefined) continue;
    const ordered = orderedCoupleMembers(u.members);
    if (ordered.length === 2) {
      const leftX = (u.x ?? 0) - (CARD_W + COUPLE_GAP) / 2;
      const rightX = (u.x ?? 0) + (CARD_W + COUPLE_GAP) / 2;
      const leftMember = ordered[0];
      const rightMember = ordered[1];
      const isYouUnit = u.id === youUnit.id;
      const leftIsYou = isYouUnit && viewerId && leftMember.id === viewerId;
      const rightIsYou = isYouUnit && viewerId && rightMember.id === viewerId;
      cards.push({
        member: displayMemberFor(leftMember),
        memberId: leftMember.id,
        isYou: !!leftIsYou,
        x: leftX,
        y: u.y,
        tier: u.tier,
        side: u.side,
        geometryRoot: u.geometryRoot,
      });
      cards.push({
        member: displayMemberFor(rightMember),
        memberId: rightMember.id,
        isYou: !!rightIsYou,
        x: rightX,
        y: u.y,
        tier: u.tier,
        side: u.side,
        geometryRoot: u.geometryRoot,
      });
      cardPosByMemberId.set(leftMember.id, {
        x: leftX,
        y: u.y,
        side: u.side,
        isYou: !!leftIsYou,
      });
      cardPosByMemberId.set(rightMember.id, {
        x: rightX,
        y: u.y,
        side: u.side,
        isYou: !!rightIsYou,
      });
    } else {
      const m = ordered[0];
      const isYouSingle = u.id === youUnit.id;
      cards.push({
        member:
          isYouSingle && !viewerId
            ? null
            : displayMemberFor(m),
        memberId:
          isYouSingle && !viewerId
            ? null
            : m.id,
        isYou: isYouSingle,
        x: u.x,
        y: u.y,
        tier: u.tier,
        side: u.side,
        geometryRoot: u.geometryRoot,
      });
      cardPosByMemberId.set(m.id, {
        x: u.x,
        y: u.y,
        side: u.side,
        isYou: isYouSingle,
      });
    }
  }

  // ─── Step 13: Overflow row for unplaceable members ────────────────────
  // Members whose tier resolved to null (kinship code 'extended' /
  // 'unknown') AND legacy fallback also failed → drop into a "Needs
  // connection" overflow row beneath the grandchildren tier.
  const placedMemberIds = new Set<string>();
  for (const c of cards) {
    if (c.member) placedMemberIds.add(c.member.id);
  }
  const overflow: GraphMember[] = [];
  for (const m of visible) {
    if (viewerId && m.id === viewerId) continue;
    if (
      !viewerId &&
      m.side === 'self' &&
      m.relationship === 'unknown'
    ) {
      continue;
    }
    if (placedMemberIds.has(m.id)) continue;
    overflow.push(m);
  }
  if (overflow.length > 0) {
    // Find lowest existing tier (most negative y → most positive y on screen).
    let lowestTier = -2;
    for (const c of cards) {
      if (c.tier < lowestTier) lowestTier = c.tier;
    }
    const overflowTier = lowestTier - 1;
    const y = overflowTier * -ROW_GAP * -1; // tier negative = positive y
    // Wait — sign convention: y increases downward; we used y = tier * ROW_GAP
    // with tier 0 at y=0, tier 1 at y=-ROW_GAP, tier -1 at y=+ROW_GAP.
    // Let's make it explicit using the same convention placeDescendants did:
    //   parent.y + ROW_GAP for descendants, so descendants have larger y.
    // YOU's y = 0 → tier 0 at y=0 → tier -1 at y=ROW_GAP → tier -2 at y=2*ROW_GAP.
    // ancestors: tier+1 at y=-ROW_GAP, tier+2 at y=-2*ROW_GAP, etc.
    const overflowY = -overflowTier * ROW_GAP;
    const slot = CARD_W + CLUSTER_GAP;
    const startX = -((overflow.length - 1) * slot) / 2;
    overflow.forEach((m, i) => {
      cards.push({
        member: displayMemberFor(m),
        memberId: m.id,
        x: startX + i * slot,
        y: overflowY,
        tier: overflowTier,
        side: sideOf(m),
        geometryRoot: 'unresolved',
        needsConnection: true,
      });
    });
  }

  /*
   * CC-TREE-LIVE-003G3Q
   *
   * Publish canonical unit lineage to cards solely as visual-cluster
   * evidence. Couple/unit members inherit the same unit lineage key.
   * Missing evidence fails closed to null.
   */
  for (const card of cards) {
    card.clusterKey =
      card.memberId
        ? unitOfMember.get(
            card.memberId,
          )?.lineageKey ?? null
        : null;
  }

  // ─── Step 14: Optional re-center on a different member ────────────────
  if (centeredMemberId) {
    const target = cards.find((c) => c.member?.id === centeredMemberId);
    if (target) {
      const dx = target.x;
      const dy = target.y;
      for (const c of cards) {
        c.x -= dx;
        c.y -= dy;
      }
    }
  }

  // ─── Step 15: Build edges (parent-midpoint → child-top) ───────────────
  // Each placed member draws ONE edge from its anchor parent unit's
  // bottom-midpoint to its own top-center. Couple parents share a
  // midpoint = the couple's unit.x. Single parents anchor at the parent's
  // own x.
  const edges: PyramidEdge[] = [];
  const seenEdge = new Set<string>();
  const seenEndpointAuthority =
    new Set<string>();

  const endpointAuthorityKey = (
    fromMemberIds: readonly string[],
    toMemberId: string,
  ): string =>
    [
      ...fromMemberIds,
      toMemberId,
    ]
      .sort()
      .join('::');

  const renderedTierByMemberId =
    new Map(
      cards.flatMap(
        card =>
          card.memberId
            ? [
                [
                  card.memberId,
                  card.tier,
                ] as const,
              ]
            : [],
      ),
    );

  const connectorObstacles =
    cards.flatMap(
      card =>
        card.memberId
          ? [
              {
                memberId:
                  card.memberId,
                centerX:
                  card.x,
                centerY:
                  card.y,
                width:
                  CARD_W,
                height:
                  CARD_H,
              },
            ]
          : [],
    );

  const connectorRootForSide = (
    side: SideKey,
  ): CanonicalTreeConnectorRoot =>
    side === 'mother'
      ? 'maternal'
      : side === 'father'
        ? 'paternal'
        : 'self';

  const addEdge = (
    fromMemberIds: string[],
    toMemberId: string,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    side: SideKey,
    verified: boolean,
    key: string,
    parentTier: number,
    childTier: number,
    endpointPurpose:
      'ORDINARY' |
      'DIRECT_PARENT_TO_VIEWER' =
        'ORDINARY',
  ) => {
    const endpointKey =
      endpointAuthorityKey(
        fromMemberIds,
        toMemberId,
      );

    if (
      seenEdge.has(key) ||
      seenEndpointAuthority.has(
        endpointKey,
      )
    ) {
      return;
    }

    seenEdge.add(key);
    seenEndpointAuthority.add(
      endpointKey,
    );

    const route =
      planCanonicalTreeConnectorCorridor({
        key,
        fromMemberIds,
        toMemberId,
        viewerMemberId:
          viewerId ?? null,
        endpointPurpose:
          endpointPurpose ===
            'DIRECT_PARENT_TO_VIEWER'
              ? 'DIRECT_PARENT_TO_VIEWER'
              : null,
        root:
          connectorRootForSide(side),
        from: {
          x:
            fromX,
          y:
            fromY,
        },
        to: {
          x:
            toX,
          y:
            toY,
        },
        parentTier,
        childTier,
        viewerCenterX:
          youUnit.x ?? 0,
        viewerEnvelopeWidth:
          widthOf(youUnit),
        rootGap:
          ROOT_BOUNDARY_GAP,
        obstacles:
          connectorObstacles,
      });

    edges.push({
      fromMemberIds:
        [...fromMemberIds],
      toMemberId,
      from: {
        x:
          fromX,
        y:
          fromY,
      },
      to: {
        x:
          toX,
        y:
          toY,
      },
      side,
      verified,
      key,
      routeRoot:
        route.root,
      routeStatus:
        route.status,
      viewerTerminalEligibility:
        route.viewerTerminalEligibility,
      routePoints:
        route.points.map(
          point => ({
            x:
              point.x,
            y:
              point.y,
          }),
        ),
      rejectedObstacleMemberIds:
        [...route.rejectedObstacleMemberIds],
    });
  };
  // For every member with an anchor parent unit on canvas, draw
  // parent-midpoint → child-top.
  const anchorParentUnitForMember = (memberId: string): Unit | null => {
    return parentUnitOfMember.get(memberId) ?? null;
  };
  // YOU has parents → edge from parents-unit midpoint to YOU.
  if (viewerId) {
    const pu = parentUnitOfMember.get(viewerId);
    if (pu && pu.x !== undefined && pu.y !== undefined) {
      const youPos = cardPosByMemberId.get(viewerId);
      if (youPos) {
        const viewerRow =
          byId.get(viewerId);

        const structuralParentIds =
          new Set(
            (
              viewerRow?.parentIds ??
              []
            ).filter(Boolean),
          );

        const authorizedParents =
          pu.members.filter(
            parent =>
              structuralParentIds.has(
                parent.id,
              ),
          );

        if (
          authorizedParents.length >
          0
        ) {
          const singletonParent =
            authorizedParents.length ===
            1
              ? cardPosByMemberId.get(
                  authorizedParents[0].id,
                ) ?? null
              : null;

          const connectorSide =
            authorizedParents.length ===
            1
              ? sideOf(
                  authorizedParents[0],
                )
              : pu.side;

          addEdge(
            authorizedParents.map(
              parent =>
                parent.id,
            ),
            viewerId,
            singletonParent?.x ??
              pu.x,
            (
              singletonParent?.y ??
              pu.y ??
              0
            ) +
              CARD_H / 2,
            youPos.x,
            youPos.y - CARD_H / 2,
            connectorSide,
            true,
            `you-parent`,
            pu.tier,
            0,
            'DIRECT_PARENT_TO_VIEWER',
          );
        }
      }
    }
  }
  for (const m of live) {
    const myPos = cardPosByMemberId.get(m.id);
    if (!myPos) continue;
    const pu = anchorParentUnitForMember(m.id);
    if (!pu || pu.x === undefined || pu.y === undefined) continue;
    // Skip if the parent unit IS YOU and we already added that edge.
    const verified = m.approvalStatus === 'approved';

    const structuralParentIds =
      new Set(
        (
          m.parentIds ??
          []
        ).filter(Boolean),
      );

    const authorizedParents =
      pu.members.filter(
        parent =>
          structuralParentIds.has(
            parent.id,
          ),
      );

    if (
      authorizedParents.length ===
      0
    ) {
      continue;
    }

    const singletonParent =
      authorizedParents.length ===
      1
        ? cardPosByMemberId.get(
            authorizedParents[0].id,
          ) ?? null
        : null;

    const connectorSide =
      viewerId &&
      authorizedParents.some(
        parent =>
          parent.id === viewerId,
      )
        ? 'self'
        : authorizedParents.length ===
            1
          ? sideOf(
              authorizedParents[0],
            )
          : pu.side;

    addEdge(
      authorizedParents.map(
        parent =>
          parent.id,
      ),
      m.id,
      singletonParent?.x ??
        pu.x,
      (
        singletonParent?.y ??
        pu.y
      ) +
        CARD_H / 2,
      myPos.x,
      myPos.y - CARD_H / 2,
      connectorSide,
      verified,
      `${pu.id}->${m.id}`,
      pu.tier,
      renderedTierByMemberId.get(m.id) ??
        (() => {
          throw new Error(
            `Missing rendered tier for connector child ${m.id}.`,
          );
        })(),
    );
  }

  // ─── Step 15b: Secondary co-parent edges ──────────────────────────────
  // MODERN-FAMILY RULE — when a child has parent_ids = [A, B] but A and
  // B are NOT linked via spouse_id (so they are NOT a locked couple unit
  // on the canvas), the anchor-parent edge above only connects ONE of
  // them. Draw an extra parent edge from each non-anchor co-parent to
  // the child so the child remains visibly attached to BOTH parents
  // without forcing a heart-line/couple visual between the parents.
  //
  // Pure rendering — never mutates parent_ids, never auto-creates
  // spouse_id, never re-orders cards. The edge is keyed independently
  // so the dedupe set treats it as a separate line from the primary
  // anchor edge.
  for (const m of live) {
    const myPos = cardPosByMemberId.get(m.id);
    if (!myPos) continue;
    const pu = anchorParentUnitForMember(m.id);
    if (!pu) continue;
    const anchorMemberIds = new Set(pu.members.map((mm) => mm.id));
    const verified = m.approvalStatus === 'approved';
    for (const pid of (m.parentIds ?? []).filter(Boolean)) {
      if (anchorMemberIds.has(pid)) continue; // already drawn
      const coParentPos = cardPosByMemberId.get(pid);
      if (!coParentPos) continue;
      addEdge(
        [pid],
        m.id,
        coParentPos.x,
        coParentPos.y + CARD_H / 2,
        myPos.x,
        myPos.y - CARD_H / 2,
        pid === viewerId
          ? 'self'
          : coParentPos.side,
        verified,
        `coparent:${pid}->${m.id}`,
        renderedTierByMemberId.get(pid) ??
          (() => {
            throw new Error(
              `Missing rendered tier for co-parent ${pid}.`,
            );
          })(),
        renderedTierByMemberId.get(m.id) ??
          (() => {
            throw new Error(
              `Missing rendered tier for shared child ${m.id}.`,
            );
          })(),
      );
    }
  }

  // YOU's secondary co-parent edges — when YOU has multiple parents but
  // they're not coupled, draw an edge from each non-anchor parent too.
  if (viewerId) {
    const youPos = cardPosByMemberId.get(viewerId);
    const youRow = byId.get(viewerId);
    const pu = parentUnitOfMember.get(viewerId);
    if (youPos && youRow && pu) {
      const anchorMemberIds = new Set(pu.members.map((mm) => mm.id));
      for (const pid of (youRow.parentIds ?? []).filter(Boolean)) {
        if (anchorMemberIds.has(pid)) continue;
        const coParentPos = cardPosByMemberId.get(pid);
        if (!coParentPos) continue;
        addEdge(
          [pid],
          viewerId,
          coParentPos.x,
          coParentPos.y + CARD_H / 2,
          youPos.x,
          youPos.y - CARD_H / 2,
          coParentPos.side,
          true,
          `coparent:${pid}->you`,
          renderedTierByMemberId.get(pid) ??
            (() => {
              throw new Error(
                `Missing rendered tier for viewer co-parent ${pid}.`,
              );
            })(),
          renderedTierByMemberId.get(viewerId) ??
            0,
          'DIRECT_PARENT_TO_VIEWER',
        );
      }
    }
  }



  // ─── F2B2B2: Canonical nonancestral connectors ───────────────────────
  const canonicalEdgeById =
    new Map(
      canonicalEdges.map(
        edge =>
          [edge.id, edge] as const,
      ),
    );

  const rootForCanonicalRole = (
    role:
      CanonicalTreeGeometryRole,
  ): CanonicalTreeConnectorRoot => {
    if (
      role.connectionKind ===
      'co-parent'
    ) {
      return 'child-mediated';
    }

    if (
      role.connectionKind ===
        'step-family' ||
      role.connectionKind ===
        'in-law'
    ) {
      return 'partner-affiliated';
    }

    return 'affiliated';
  };

  for (
    const role of
    canonicalGeometryRoles.values()
  ) {
    if (
      !role.requiresCanonicalConnector ||
      !role.anchorMemberId ||
      !role.anchorEdgeId
    ) {
      continue;
    }

    if (
      !canonicalEdgeById.has(
        role.anchorEdgeId,
      )
    ) {
      continue;
    }

    const from =
      cardPosByMemberId.get(
        role.anchorMemberId,
      );

    const to =
      cardPosByMemberId.get(
        role.memberId,
      );

    if (!from || !to) continue;

    const fromTier =
      renderedTierByMemberId.get(
        role.anchorMemberId,
      );

    const toTier =
      renderedTierByMemberId.get(
        role.memberId,
      );

    if (
      fromTier === undefined ||
      toTier === undefined ||
      fromTier !== toTier
    ) {
      continue;
    }

    const key =
      `canonical:${role.anchorEdgeId}:` +
      `${role.anchorMemberId}->${role.memberId}`;

    const endpointKey =
      endpointAuthorityKey(
        [role.anchorMemberId],
        role.memberId,
      );

    if (
      seenEdge.has(key) ||
      seenEndpointAuthority.has(
        endpointKey,
      )
    ) {
      continue;
    }

    seenEdge.add(key);
    seenEndpointAuthority.add(
      endpointKey,
    );

    const route =
      planCanonicalTreeLateralConnector({
        key,
        fromMemberId:
          role.anchorMemberId,
        toMemberId:
          role.memberId,
        root:
          rootForCanonicalRole(role),
        from: {
          x: from.x,
          y: from.y,
        },
        to: {
          x: to.x,
          y: to.y,
        },
        cardWidth:
          CARD_W,
        cardHeight:
          CARD_H,
        clearance:
          24,
        obstacles:
          connectorObstacles,
      });

    edges.push({
      fromMemberIds:
        [role.anchorMemberId],
      toMemberId:
        role.memberId,
      from: {
        x: from.x,
        y: from.y,
      },
      to: {
        x: to.x,
        y: to.y,
      },
      side:
        to.side,
      verified:
        role.confidence !==
        'unresolved',
      key,
      routeRoot:
        route.root,
      routeStatus:
        route.status,

      /*
       * This edge is governed by the lateral connector
       * authority, not the ancestry corridor.
       */
      viewerTerminalEligibility:
        null,

      routePoints:
        route.points.map(
          point => ({
            x: point.x,
            y: point.y,
          }),
        ),
      rejectedObstacleMemberIds:
        [
          ...route
            .rejectedObstacleMemberIds,
        ],
    });
  }

  // ─── Step 16: Spouse links (for the heart icon between couple cards) ──
  const spouseLinks: PyramidSpouseLink[] = [];
  const seenCoupleId = new Set<string>();
  for (const u of allUnits) {
    if (u.members.length !== 2) continue;
    if (u.x === undefined || u.y === undefined) continue;
    if (seenCoupleId.has(u.id)) continue;
    seenCoupleId.add(u.id);
    const ordered = orderedCoupleMembers(u.members);
    const leftMember = ordered[0];
    const rightMember = ordered[1];
    const leftX = u.x - (CARD_W + COUPLE_GAP) / 2;
    const rightX = u.x + (CARD_W + COUPLE_GAP) / 2;

    spouseLinks.push({
      aMemberId: leftMember.id,
      bMemberId: rightMember.id,
      a: { x: leftX, y: u.y },
      b: { x: rightX, y: u.y },
    });
  }

  // ─── Step 17: Bounds ──────────────────────────────────────────────────
  let minX = -CARD_W / 2;
  let maxX = CARD_W / 2;
  let minY = -CARD_H / 2;
  let maxY = CARD_H / 2;
  for (const c of cards) {
    minX = Math.min(minX, c.x - CARD_W / 2);
    maxX = Math.max(maxX, c.x + CARD_W / 2);
    minY = Math.min(minY, c.y - CARD_H / 2);
    maxY = Math.max(maxY, c.y + CARD_H / 2);
  }

  for (const edge of edges) {
    for (const point of edge.routePoints) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  for (const link of spouseLinks) {
    for (const point of [link.a, link.b]) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  const bounds: PyramidBounds = {
    minX,
    maxX,
    minY,
    maxY,
    w: maxX - minX,
    h: maxY - minY,
  };
  // Generation rows are immutable after tier placement.
  //
  // Cluster collision handling must expand horizontally through governed
  // branch intervals. It must never move a card vertically after its
  // generation tier has been assigned.

  const renderedYouCard =
    cards.find((card) => card.isYou) ?? null;

  const renderedYouMemberId =
    renderedYouCard?.memberId ?? null;

  const viewerCenterEvidence: ViewerCenterEvidence = {
    requestedViewerMemberId: viewerMemberId,
    resolvedStructuralViewerId: viewerId,
    renderedYouMemberId,
    renderedYouX: renderedYouCard?.x ?? null,
    renderedYouY: renderedYouCard?.y ?? null,
    centeredMemberId,
    isRenderedCenterCorrect:
      renderedYouCard !== null &&
      (
        viewerMemberId === null ||
        (
          viewerId === viewerMemberId &&
          renderedYouMemberId === viewerMemberId
        )
      ) &&
      (
        centeredMemberId !== null ||
        (
          Math.abs(renderedYouCard.x) <= 0.001 &&
          Math.abs(renderedYouCard.y) <= 0.001
        )
      ),
    failureReason:
      renderedYouCard === null
        ? 'rendered-you-missing'
        : centeredMemberId === null &&
            (
              Math.abs(renderedYouCard.x) > 0.001 ||
              Math.abs(renderedYouCard.y) > 0.001
            )
          ? 'rendered-you-displaced'
          : null,
  };

  return {
    cards,
    edges,
    spouseLinks,
    bounds,
    viewerCenterEvidence,
  };
}
